/**
 * Tool Registry — single source of truth for what Unveil contains.
 *
 * Two sides, Code and Occult (ROOMS), each divided into worlds (GROUPS):
 * the families of systems a practitioner works in — ciphers, encodings,
 * gematria, astrology, Kabbalah… Every tool belongs to exactly one world.
 *
 * The per-side sidebar, search, the tool header, SEO metadata and
 * cross-tool "Open in…" menus are all derived from these arrays.
 *
 * To add a tool: add ONE entry to TOOLS (plus SEO_DESCRIPTIONS / TOOL_RELEVANCE).
 * Pure data with lazy imports only — safe to import from Node tests.
 */

// ─── Sides ───────────────────────────────────────────────────────────────────

export const ROOMS = [
  { id: 'code', label: 'Code' },
  { id: 'occult', label: 'Occult' },
];

// ─── Worlds ──────────────────────────────────────────────────────────────────
// `about`: one sentence on what the world holds, shown under the Room / World
// line of each of its tools' headers and in their static pages (2026-10-21) —
// never a page that lists the tools.

export const GROUPS = [
  // Code
  {
    id: 'ciphers',
    room: 'code',
    label: 'Ciphers',
    about: 'Classical and machine ciphers, from Caesar’s shift and the ACA’s 62 types to Enigma and Lorenz, and text as notes: enciphered with a key, chained, many broken without one.',
  },
  {
    id: 'cryptanalysis',
    room: 'code',
    label: 'Cryptanalysis',
    about: 'Reading a text without its key: naming the cipher, peeling off encodings, counting letters, breaking nomenclators, replaying the Enigma breaks of the 1930s and 1940s, checking anagrams.',
  },
  {
    id: 'encodings',
    room: 'code',
    label: 'Encodings',
    about: 'Ways of writing that hide nothing by design — number bases, Unicode, Morse, Braille and signal codes, QR codes, Roman numerals, computer timestamps — read and written both ways.',
  },
  {
    id: 'stego',
    room: 'code',
    label: 'Steganography',
    about: 'Messages hidden in plain sight, in words, invisible characters, pixels, sound, printed dots, stacked transparencies, DNA and the bytes of files, and the analyses that find them.',
  },
  {
    id: 'crypto',
    room: 'code',
    label: 'Modern crypto',
    about: 'The cryptography of computers: hashes, AES and HMAC, RSA and its weak keys, Diffie–Hellman, Shor and Grover on a quantum computer, ML-KEM, BB84, Shamir’s shares, time locks, passwords.',
  },
  {
    id: 'text',
    room: 'code',
    label: 'Text',
    about: 'Work on the text itself: regular expressions, the differences between two texts, words found by their letter pattern, and word breaks put back into unspaced text.',
  },
  {
    id: 'archive',
    room: 'code',
    label: 'Archive',
    about: 'Historical and unsolved ciphertexts with their origin, status and sources, each opening in the tool that reads it.',
  },
  // Occult
  {
    id: 'astrology',
    room: 'occult',
    label: 'Astrology',
    about: 'Charts and the sky from computed positions: natal, transits, progressions, Hellenistic and Vedic techniques, the Chinese four pillars, fixed stars, mansions and hours read in period sources, and world maps.',
  },
  {
    id: 'numbers',
    room: 'occult',
    label: 'Gematria & numerology',
    about: 'Letters read as numbers — Hebrew gematria, Greek isopsephy, Arabic abjad — numerology as Balliett and Cheasley printed it, and the sequences of number theory.',
  },
  {
    id: 'divination',
    room: 'occult',
    label: 'Divination',
    about: 'Casting and reading by lot: the I Ching, the tarot, geomancy and the lot-books of dice and number, each read in the words of its early printed sources.',
  },
  {
    id: 'kabbalah',
    room: 'occult',
    label: 'Kabbalah',
    about: 'The Tree of Life and the letters of the Hebrew Bible: correspondences, letter exchanges, equidistant letter sequences, the 231 gates and the 72 Names.',
  },
  {
    id: 'symbols',
    room: 'occult',
    label: 'Symbols & scripts',
    about: 'Historical scripts and signs taken from their sources: runes, ogham, Agrippa’s alphabets and Enochian, Dee’s Great Table, symbols, sigils and sacred geometry.',
  },
  {
    id: 'alchemy',
    room: 'occult',
    label: 'Alchemy & Hermetica',
    about: 'Alchemy and the Hermetic writings in their prints: the chemists’ signs, Maier’s emblems and fugues, the Emerald Tablet’s versions, the Chymical Wedding’s riddles, Dee’s monad, Agrippa’s squares and sigils.',
  },
  {
    id: 'calendars',
    room: 'occult',
    label: 'Calendars',
    about: 'A date across sixteen calendars and the Masonic eras, with the numbers and signs it carries; Easter and the molad by the old rules; a time in every zone.',
  },
];

// ─── Tools ───────────────────────────────────────────────────────────────────
// Order within a world is the order shown everywhere. Summaries are short
// technical descriptions (search, tooltips).
//
// Flags:
//   textInput      — accepts text from "Open in…"
//   primaryInput   — selector for the input that receives that text
//   handlesPrefill — tool listens for `unveil:prefill` itself (e.g. dates)

export const TOOLS = [
  // ── Code — Ciphers ──
  {
    id: 'caesar',
    label: 'Shift Ciphers',
    group: 'ciphers',
    summary: 'Caesar, ROT13, Atbash and affine',
    textInput: true,
    tags: ['caesar', 'rot13', 'atbash', 'shift', 'rotation', 'affine', 'mathematical', 'modular', 'coprime'],
    load: () => import('./caesar.js').then((m) => m.initCaesar()),
  },
  {
    id: 'substitution',
    label: 'Substitution',
    group: 'ciphers',
    summary: 'Simple and homophonic substitution, with solvers',
    textInput: true,
    tags: ['monoalphabetic', 'mapping', 'alphabet', 'cryptogram', 'auto solve', 'hill climbing', 'homophonic', 'zodiac', 'z408', 'z340', 'nomenclator'],
    load: () => import('./substitution.js').then((m) => m.initSubstitution()),
  },
  {
    id: 'vigenere',
    label: 'Vigenère',
    group: 'ciphers',
    summary: 'Vigenère, Beaufort, Autokey, Gronsfeld and Porta',
    textInput: true,
    tags: ['polyalphabetic', 'key cipher', 'tableau', 'beaufort', 'autokey', 'gronsfeld', 'porta', 'bellaso'],
    load: () => import('./vigenere.js').then((m) => m.initVigenere()),
  },
  {
    id: 'transposition',
    label: 'Transposition',
    group: 'ciphers',
    summary: 'Rail fence, columnar, route, scytale and grille',
    textInput: true,
    tags: [
      'railfence',
      'zigzag',
      'fence',
      'columnar',
      'transposition',
      'keyword',
      'columns',
      'grid',
      'route',
      'spiral',
      'scytale',
      'cylinder',
      'sparta',
    ],
    load: () => import('./transposition.js').then((m) => m.initTransposition()),
  },
  {
    id: 'playfair',
    label: 'Playfair',
    group: 'ciphers',
    summary: 'Playfair and Four-Square, with a solver',
    textInput: true,
    tags: ['digraph', 'matrix', 'grid', 'four square', 'wheatstone', 'solver', 'simulated annealing'],
    load: () => import('./playfair.js').then((m) => m.initPlayfair()),
  },
  {
    id: 'gridciphers',
    label: 'Grid Ciphers',
    group: 'ciphers',
    summary: 'Bifid, Trifid, ADFGVX and Hill, solved keyless; Nihilist',
    textInput: true,
    tags: ['bifid', 'adfgvx', 'adfgx', 'wwi', 'fractionation', 'polybius', 'grid', 'trifid', '3d', 'hill', 'matrix', 'nihilist', 'straddling checkerboard', 'checkerboard', 'solver', 'without the key', 'ciphertext only', 'painvin', 'delastelle', 'known plaintext'],
    load: () => import('./gridciphers.js').then((m) => m.initGridCiphers()),
  },
  {
    id: 'enigma',
    label: 'Enigma',
    group: 'ciphers',
    summary: 'Rotor machine, and the Bombe to break it',
    textInput: true,
    tags: ['rotor', 'wwii', 'german', 'machine', 'bletchley', 'bombe', 'turing', 'welchman', 'crib', 'without the key', 'ciphertext only', 'plugboard', 'enigma k', 'commercial enigma', 'railway enigma', 'rocket', 'enigma g', 'abwehr', 'g-312', 'swiss k'],
    load: () => import('./enigma.js').then((m) => m.initEnigma()),
  },
  {
    id: 'hagelin',
    label: 'Hagelin M-209',
    group: 'ciphers',
    summary: 'The US Army’s pin-and-lug machine, solved without its key',
    textInput: true,
    primaryInput: '#hgl-input',
    tags: ['m-209', 'hagelin', 'converter', 'pin wheel', 'lug', 'drum', 'wwii', 'us army', 'machine', 'tm 11-380', 'known plaintext', 'ciphertext only', 'lasry'],
    load: () => import('./hagelin.js').then((m) => m.initHagelin()),
  },
  {
    id: 'lorenz',
    label: 'Lorenz SZ42',
    group: 'ciphers',
    summary: 'Tunny, and Colossus to set its wheels',
    textInput: true,
    tags: ['lorenz', 'tunny', 'sz40', 'sz42', 'teleprinter', 'baudot', 'ita2', 'bletchley', 'colossus', 'wwii', 'machine'],
    load: () => import('./lorenz.js').then((m) => m.initLorenz()),
  },
  {
    id: 'purple',
    label: 'Purple',
    group: 'ciphers',
    summary: 'Japan’s diplomatic machine, broken from its ciphertext',
    textInput: true,
    primaryInput: '#pur-input',
    tags: ['purple', 'angooki taipu b', '97-shiki obun injiki', 'type b', 'japanese', 'diplomatic', 'pearl harbor', '14-part message', 'magic', 'sis', 'friedman', 'rowlett', 'sixes', 'twenties', 'stepping switch', 'machine', 'wwii', 'ciphertext only'],
    load: () => import('./purple.js').then((m) => m.initPurple()),
  },
  {
    id: 'sigaba',
    label: 'SIGABA',
    group: 'ciphers',
    summary: 'The ECM Mark II, its message setting from known text',
    textInput: true,
    primaryInput: '#sig-input',
    tags: ['sigaba', 'ecm mark ii', 'ecm', 'csp-889', 'csp-888', 'csp-2900', 'm-134-c', 'rotor', 'machine', 'us navy', 'us army', 'wwii', 'pekelney', 'lasry', 'meet in the middle', 'known plaintext', 'index rotors'],
    load: () => import('./sigaba.js').then((m) => m.initSigaba()),
  },
  {
    id: 'typex',
    label: 'Typex',
    group: 'ciphers',
    summary: 'Britain’s machine, its real drums, read from a crib',
    textInput: true,
    primaryInput: '#tpx-input',
    tags: ['typex', 'type x', 'type-x', 'r5w', 'rotor', 'machine', 'british', 'raf', 'royal navy', 'mark ii', 'mark 22', 'y-269', 'op-20-g', 'stators', 'reversible inserts', 'crib', 'wwii'],
    load: () => import('./typex.js').then((m) => m.initTypex()),
  },
  {
    id: 'cylinder',
    label: 'M-94 Cylinder',
    group: 'ciphers',
    summary: 'The Jefferson–Bazeries wheel cipher as the Army used it',
    textInput: true,
    tags: ['m-94', 'jefferson', 'wheel cipher', 'jefferson disk', 'bazeries', 'cylinder', 'disk', 'us army'],
    load: () => import('./cylinder.js').then((m) => m.initCylinder()),
  },
  {
    id: 'chaocipher',
    label: 'Chaocipher',
    group: 'ciphers',
    summary: 'Byrne’s two-wheel cipher, its alphabets from known text',
    textInput: true,
    tags: ['chaocipher', 'byrne', 'exhibit 1', 'silent years', 'autokey', 'machine', 'known plaintext', 'cowan', 'maze'],
    load: () => import('./chaocipher.js').then((m) => m.initChaocipher()),
  },
  {
    id: 'vic',
    label: 'VIC Cipher',
    group: 'ciphers',
    summary: 'Häyhänen’s Soviet hand cipher, keys to groups',
    textInput: true,
    tags: ['vic', 'hayhanen', 'häyhänen', 'soviet', 'kgb', 'straddling checkerboard', 'chain addition', 'disrupted transposition', 'hollow nickel', 'spy'],
    load: () => import('./vic.js').then((m) => m.initVic()),
  },
  {
    id: 'solitaire',
    label: 'Solitaire',
    group: 'ciphers',
    summary: 'Schneier’s cipher for a deck of cards (Pontifex)',
    textInput: true,
    primaryInput: '#sol-input',
    tags: ['solitaire', 'pontifex', 'schneier', 'cryptonomicon', 'stephenson', 'deck of cards', 'playing cards', 'jokers', 'keystream', 'stream cipher', 'hand cipher'],
    load: () => import('./solitaire.js').then((m) => m.initSolitaire()),
  },
  {
    id: 'visualciphers',
    label: 'Symbol Ciphers',
    group: 'ciphers',
    summary: 'Symbol alphabets from their prints, and Baconian',
    textInput: true,
    // The Pigpen tab before 2026-10-25: its text, and the modern Pigpen in the alphabets that replaced it
    renamedFields: { 'vis-pig-input': 'vis-sym-input' },
    renamedChoices: { 'vis-tabs': { pigpen: { to: 'alphabets', also: { 'vis-sym-alphabet': 'pigpen' } } } },
    tags: [
      'symbol alphabet',
      'dancing men',
      'conan doyle',
      'sherlock holmes',
      'masonic',
      'freemasons cipher',
      'geometric',
      'symbol',
      'pigpen',
      'porta',
      'digraphic',
      'stave cipher',
      'trithemius',
      'polygraphia',
      'tironian notes',
      'frankish alphabet',
      'alchemist alphabet',
      'templar',
      'gold-bug',
      'poe',
      'cistercian numerals',
      'monks numerals',
      'agrippa',
      'bacon',
      'baconian',
      'binary',
      'steganography',
      'ab',
    ],
    load: () => import('./visualciphers.js').then((m) => m.initVisualCiphers()),
  },
  {
    id: 'bookcipher',
    label: 'Book Cipher',
    group: 'ciphers',
    summary: 'Ottendorf, book ciphers and code tables',
    textInput: true,
    primaryInput: '#bc-input',
    tags: ['ottendorf', 'reference', 'coordinate', 'position', 'beale', 'arnold', 'nomenclator', 'code table', 'codebook', 'great cipher', 'rossignol', 'bazeries'],
    load: () => import('./bookcipher.js').then((m) => m.initBookCipher()),
  },
  {
    id: 'musical',
    label: 'Musical Cryptograms',
    group: 'ciphers',
    summary: 'Text as notes: BACH, soggetto cavato, Schott, Haydn',
    textInput: true,
    primaryInput: '#mus-input',
    tags: ['music', 'musical cryptogram', 'musical cipher', 'notes', 'bach', 'b-a-c-h', 'abegg', 'soggetto cavato', 'josquin', 'zarlino', 'kircher', 'schott', 'michael haydn', 'ravel', 'haydn', 'solmization', 'staff', 'melody'],
    load: () => import('./musical.js').then((m) => m.initMusical()),
  },
  {
    id: 'aca',
    label: 'ACA Cipher Types',
    group: 'ciphers',
    summary: 'The ACA’s 62 cipher types, with examples and solvers',
    textInput: true,
    primaryInput: '#aca-input',
    tags: [
      'aca',
      'american cryptogram association',
      'cryptogram',
      'cipher types',
      'amsco',
      'cadenus',
      'myszkowski',
      'nicodemus',
      'quagmire',
      'ragbaby',
      'portax',
      'slidefair',
      'gromark',
      'progressive key',
      'redefence',
      'swagman',
      'sequence transposition',
      'phillips',
      'seriated playfair',
      'tri-square',
      'two-square',
      'digrafid',
      'cm bifid',
      'bazeries',
      'checkerboard',
      'condi',
      'nihilist',
      'fractionated morse',
      'morbit',
      'pollux',
      'monome-dinome',
      'compressocrat',
      'numbered key',
      'key phrase',
      'grandpre',
      'headlines',
      'syllabary',
      'tridigital',
      'solver',
      'without the key',
    ],
    load: () => import('./aca.js').then((m) => m.initAca()),
  },
  {
    id: 'chain',
    label: 'Cipher Chain',
    group: 'ciphers',
    summary: 'A message through steps in turn, and back',
    textInput: true,
    tags: ['chain', 'chain', 'layers', 'product cipher', 'superencipherment', 'route', 'transform', 'recipe', 'compose'],
    load: () => import('./chain.js').then((m) => m.initChain()),
  },

  // ── Code — Cryptanalysis ──
  {
    id: 'cipherid',
    label: 'Cipher Identifier',
    group: 'cryptanalysis',
    summary: 'Identify a cipher, encoding or hash and auto-solve',
    textInput: true,
    tags: [
      'identify',
      'detect',
      'classify',
      'recognize',
      'cipher id',
      'hash',
      'md5',
      'sha256',
      'bcrypt',
      'base32',
      'base58',
      'base85',
      'jwt',
      'auto solve',
    ],
    load: () => import('./cipherid.js').then((m) => m.initCipherId()),
  },
  {
    id: 'layers',
    label: 'Layer Decoder',
    group: 'cryptanalysis',
    summary: 'Peel nested encodings until the text reads',
    textInput: true,
    tags: [
      'magic',
      'auto decode',
      'recursive',
      'html entities',
      'nested',
      'layers',
      'base64',
      'hex',
      'rot13',
      'url decode',
      'gzip',
      'xor',
      'ctf',
      'flag',
      'onion',
      'decode chain',
    ],
    load: () => import('./layers.js').then((m) => m.initLayers()),
  },
  {
    id: 'cryptanalysis',
    label: 'Frequency Analysis',
    group: 'cryptanalysis',
    summary: 'Frequencies, IC, entropy, Kasiski and text counts',
    textInput: true,
    tags: [
      'cryptanalysis',
      'frequency',
      'entropy',
      'coincidence',
      'kasiski',
      'ioc',
      'statistics',
      'autocorrelation',
      'al-kindi',
      'word count',
      'character count',
      'letter count',
    ],
    load: () => import('./cryptanalysis.js').then((m) => m.initCryptanalysis()),
  },
  {
    id: 'randomness',
    label: 'Randomness Tests',
    group: 'cryptanalysis',
    summary: 'Is it random? NIST’s fifteen statistical tests',
    tags: [
      'randomness',
      'random',
      'nist',
      'sp 800-22',
      'statistical test suite',
      'p-value',
      'monobit',
      'runs test',
      'fips 140-1',
      'keystream',
      'pseudorandom',
      'rng',
    ],
    load: () => import('./randomness.js').then((m) => m.initRandomness()),
  },
  {
    id: 'nomenclator',
    label: 'Nomenclator',
    group: 'cryptanalysis',
    summary: 'Break a code table: homophones, nulls and code words',
    textInput: true,
    tags: [
      'nomenclator',
      'code table',
      'homophonic',
      'nulls',
      'code words',
      'logogram',
      'copiale',
      'mary stuart',
      'mary queen of scots',
      'borg cipher',
      'diplomatic cipher',
      'early modern',
      'transcription',
      'french',
      'latin',
      'german',
      'italian',
      'spanish',
    ],
    load: () => import('./nomenclator.js').then((m) => m.initNomenclator()),
  },
  {
    id: 'cyclometer',
    label: 'Cyclometer',
    group: 'cryptanalysis',
    summary: 'Rejewski’s break: Enigma keys from a day’s indicators',
    tags: [
      'rejewski',
      'cyclometer',
      'card catalogue',
      'characteristic',
      'enigma',
      'polish cipher bureau',
      'biuro szyfrów',
      'indicator',
      'double encipherment',
      'message key',
      'permutation',
      'cycle structure',
      'plugboard',
      'reflector a',
      '1930s',
      'kuhl',
    ],
    load: () => import('./cyclometer.js').then((m) => m.initCyclometer()),
  },
  {
    id: 'banburismus',
    label: 'Banburismus',
    group: 'cryptanalysis',
    summary: 'Turing’s attack on the naval Enigma, replayed',
    tags: [
      'banburismus',
      'turing',
      'hut 8',
      'bletchley park',
      'naval enigma',
      'kriegsmarine',
      'deciban',
      'weight of evidence',
      'scritchmus',
      'scritching',
      'banbury sheets',
      'depth',
      'grundstellung',
      'bigram tables',
      'alexander',
    ],
    load: () => import('./banburismus.js').then((m) => m.initBanburismus()),
  },
  {
    id: 'unicity',
    label: 'Unicity Distance',
    group: 'cryptanalysis',
    summary: 'How much ciphertext fixes the key: Shannon’s measure',
    tags: [
      'unicity distance',
      'unicity point',
      'shannon',
      'redundancy',
      'entropy of english',
      'information theory',
      'spurious keys',
      'key equivocation',
      'guessing game',
      'prediction',
      'key space',
    ],
    load: () => import('./unicity.js').then((m) => m.initUnicity()),
  },
  {
    id: 'lindiff',
    label: 'Linear & Differential',
    group: 'cryptanalysis',
    summary: 'Heys’s cipher broken by linear and differential analysis',
    tags: [
      'linear cryptanalysis',
      'differential cryptanalysis',
      'heys',
      'matsui',
      'biham',
      'substitution-permutation network',
      'spn',
      'block cipher',
      's-box',
      'linear approximation table',
      'difference distribution table',
      'piling-up lemma',
      'des',
    ],
    load: () => import('./lindiff.js').then((m) => m.initLinDiff()),
  },
  {
    id: 'anagram',
    label: 'Anagram',
    group: 'cryptanalysis',
    summary: 'Words in a set of letters, and readings checked',
    textInput: true,
    primaryInput: '#anag-input',
    // Tabs since 2026-10-23, Find first; a link or session from before holds letters to find words in
    addedChoices: { 'anag-tabs': 'find' },
    tags: ['rearrange', 'scramble', 'word', 'unscramble', 'multi-word', 'priority anagram', 'galileo', 'kepler', 'huygens', 'hooke', 'newton', 'ut tensio sic vis', 'letter count'],
    load: () => import('./anagram.js').then((m) => m.initAnagram()),
  },

  // ── Code — Encodings ──
  {
    id: 'converter',
    label: 'Base Converter',
    group: 'encodings',
    summary: 'Binary, hex, Base64/32/58/85, URL and JWT',
    textInput: true,
    handlesPrefill: true,
    tags: [
      'binary',
      'octal',
      'hex',
      'decimal',
      'hexdump',
      'radix',
      'base64',
      'base32',
      'base58',
      'base85',
      'jwt',
      'encode',
      'decode',
      'url',
      'percent',
      'uri',
      'escape',
    ],
    load: () => import('./converter.js').then((m) => m.initConverter()),
  },
  {
    id: 'ascii',
    label: 'Character Map',
    group: 'encodings',
    summary: 'Code points and Unicode properties',
    textInput: true,
    handlesPrefill: true,
    tags: ['ascii', 'unicode', 'character', 'table', 'code point'],
    load: () => import('./ascii.js').then((m) => m.initAscii()),
  },
  {
    id: 'morse',
    label: 'Morse',
    group: 'encodings',
    summary: 'Morse code with audio',
    textInput: true,
    handlesPrefill: true,
    tags: ['code', 'dots', 'dashes', 'telegraph'],
    load: () => import('./morse.js').then((m) => m.initMorse()),
  },
  {
    id: 'puzzlecodes',
    label: 'Puzzle Codes',
    group: 'encodings',
    summary: 'Braille, semaphore, flags, NATO, tap code and keypad',
    textInput: true,
    handlesPrefill: true,
    tags: [
      'nato',
      'nato alphabet',
      'phonetic alphabet',
      'spelling alphabet',
      'alpha bravo',
      'tap code',
      'knock code',
      'polybius',
      'prisoner',
      'braille',
      'ueb',
      'grade 1',
      'semaphore',
      'flag semaphore',
      'phone keypad',
      'keypad',
      't9',
      'multi-tap',
      'multitap',
      'sms',
      'e.161',
      'signal flags',
      'maritime flags',
      'nautical flags',
      'international code of signals',
      'geocaching',
      'escape room',
      'puzzle hunt',
    ],
    load: () => import('./puzzlecodes.js').then((m) => m.initPuzzleCodes()),
  },
  {
    id: 'qrcode',
    label: 'QR Code',
    group: 'encodings',
    summary: 'QR codes made and read, their structure shown',
    textInput: true,
    primaryInput: '#qr-input',
    tags: ['qr', 'qr code', 'quick response', '2d barcode', 'barcode', 'matrix code', 'iso 18004', 'reed-solomon', 'error correction', 'mask', 'denso wave', 'scan', 'decode', 'generate'],
    load: () => import('./qrcode.js').then((m) => m.initQrCode()),
  },
  {
    id: 'timestamps',
    label: 'Timestamps',
    group: 'encodings',
    summary: 'Unix, GPS, FILETIME, Excel and other computer timestamps',
    textInput: true,
    primaryInput: '#tsp-input',
    handlesPrefill: true,
    // Tabs since 2026-10-21, Date → timestamp first; a link or session from before holds a timestamp
    addedChoices: { 'tsp-tabs': 'timestamp' },
    tags: [
      'unix',
      'epoch',
      'timestamp',
      'seconds',
      'milliseconds',
      'gps time',
      'filetime',
      'excel serial',
      'ntp',
      'webkit',
      'chrome',
      'ticks',
      '.net',
      'julian date',
      'forensics',
      'ctf',
    ],
    load: () => import('./timestamps.js').then((m) => m.initTimestamps()),
  },
  {
    id: 'roman',
    label: 'Roman Numerals',
    group: 'encodings',
    summary: 'Roman to Arabic and back',
    textInput: true,
    tags: ['numeral', 'convert', 'roman numeral'],
    load: () => import('./roman.js').then((m) => m.initRoman()),
  },

  // ── Code — Steganography ──
  {
    id: 'zerowidth',
    label: 'Text Stego',
    group: 'stego',
    summary: 'Zero-width, SNOW whitespace, homoglyphs; inspect any text',
    textInput: true,
    handlesPrefill: true,
    tags: [
      'steganography',
      'invisible',
      'hidden',
      'unicode',
      'zero-width',
      'snow',
      'whitespace',
      'homoglyph',
      'confusables',
      'unicode tags',
      'variation selectors',
      'bidi',
      'trojan source',
      'ave maria',
      'trithemius',
      'polygraphia',
      'secret',
      'stego',
    ],
    load: () => import('./zerowidth.js').then((m) => m.initZeroWidth()),
  },
  {
    id: 'lsb',
    label: 'Image Stego',
    group: 'stego',
    summary: 'Least-significant-bit image steganography',
    tags: ['steganography', 'image', 'pixel', 'lsb', 'least significant bit', 'hidden', 'stego', 'canvas'],
    load: () => import('./lsb.js').then((m) => m.initLSB()),
  },
  {
    id: 'audiostego',
    label: 'Audio Stego',
    group: 'stego',
    summary: 'Spectrograms, hidden bits, keypad tones, SSTV, Morse',
    tags: ['audio', 'spectrogram', 'sound', 'wav', 'lsb', 'dtmf', 'touch tone', 'phone tones', 'hidden image', 'steganography', 'goertzel', 'fft'],
    load: () => import('./audiostego.js').then((m) => m.initAudioStego()),
  },
  {
    id: 'fileforensics',
    label: 'File Forensics',
    group: 'stego',
    summary: 'A file’s real type, metadata and what it hides',
    handlesPrefill: true,
    tags: [
      'forensics',
      'file',
      'magic bytes',
      'file signature',
      'exif',
      'metadata',
      'gps',
      'polyglot',
      'appended data',
      'binwalk',
      'strings',
      'entropy',
      'hex viewer',
      'png chunks',
      'zip',
      'pdf javascript',
    ],
    load: () => import('./fileforensics.js').then((m) => m.initFileForensics()),
  },
  {
    id: 'nullcipher',
    label: 'Null Cipher',
    group: 'stego',
    summary: 'Extract hidden letters from cover text',
    textInput: true,
    tags: [
      'null cipher',
      'concealment cipher',
      'acrostic',
      'hidden message',
      'cover text',
      'first letter',
      'capitals',
      'steganography',
    ],
    load: () => import('./nullcipher.js').then((m) => m.initNullCipher()),
  },
  {
    id: 'acrostics',
    label: 'Acrostic Search',
    group: 'stego',
    summary: 'Acrostics in long texts and Booth’s string, against chance',
    textInput: true,
    handlesPrefill: true,
    primaryInput: '#acr-word',
    tags: ['acrostic', 'telestich', 'first letters', 'line initials', 'virgil', 'aratus', 'leptē', 'shakespeare', 'first folio', 'sonnets', 'bacon', 'booth', 'friedman', 'alphabetic acrostic', 'psalm 119', 'lamentations', 'chance', 'hidden message'],
    load: () => import('./acrostics.js').then((m) => m.initAcrostics()),
  },
  {
    id: 'steganographia',
    label: 'Steganographia',
    group: 'stego',
    summary: 'Trithemius’s book of secret writing, its hidden texts read',
    tags: ['steganographia', 'trithemius', 'johannes trithemius', 'clavis', 'spirits', 'conjurations', 'angels', 'number tables', 'jim reeds', 'thomas ernst', 'book iii', '1606', 'planetary angels', 'orifiel', 'hidden message'],
    load: () => import('./steganographia.js').then((m) => m.initSteganographia()),
  },
  {
    id: 'visualcrypto',
    label: 'Visual Cryptography',
    group: 'stego',
    summary: 'A picture split into shares that show it only when stacked',
    tags: [
      'visual cryptography',
      'visual secret sharing',
      'naor',
      'shamir',
      'naor shamir',
      'transparencies',
      'secret sharing',
      'threshold scheme',
      'k out of n',
      'subpixels',
      'iacr',
      'eurocrypt',
      'hidden image',
      'steganography',
    ],
    load: () => import('./visualcrypto.js').then((m) => m.initVisualCrypto()),
  },
  {
    id: 'trackingdots',
    label: 'Tracking Dots',
    group: 'stego',
    summary: 'A colour printer’s yellow dots: serial number, date, time',
    tags: ['tracking dots', 'yellow dots', 'printer steganography', 'machine identification code', 'docucolor', 'xerox', 'eff', 'colour laser printer', 'printer serial number', 'forensic', 'microdots', 'parity'],
    load: () => import('./trackingdots.js').then((m) => m.initTrackingDots()),
  },
  {
    id: 'dnastego',
    label: 'DNA Stego',
    group: 'stego',
    summary: 'A message hidden in DNA, found again by its primers',
    tags: [
      'dna',
      'dna steganography',
      'dna microdot',
      'microdot',
      'genetic',
      'genome',
      'codon',
      'primer',
      'pcr',
      'clelland',
      'bancroft',
      'normandy',
      'nucleotide',
      'base pairs',
      'steganography',
    ],
    load: () => import('./dnastego.js').then((m) => m.initDnaStego()),
  },

  // ── Code — Modern crypto ──
  {
    id: 'hash',
    label: 'Hash Generator',
    group: 'crypto',
    summary: 'MD5 and SHA digests',
    textInput: true,
    tags: ['md5', 'sha', 'sha256', 'checksum', 'digest'],
    load: () => import('./hash.js').then((m) => m.initHash()),
  },
  {
    id: 'xor',
    label: 'XOR & One-Time Pad',
    group: 'crypto',
    summary: 'Bitwise XOR and Vernam',
    textInput: true,
    primaryInput: '#xor-a',
    tags: ['bitwise', 'exclusive or', 'binary', 'vernam', 'otp', 'one-time pad', 'xor'],
    load: () => import('./xor.js').then((m) => m.initXor()),
  },
  {
    id: 'aes',
    label: 'AES & HMAC',
    group: 'crypto',
    summary: 'AES-GCM, CBC and CTR encryption, plus HMAC',
    textInput: true,
    primaryInput: '#aes-input',
    tags: [
      'aes',
      'gcm',
      'cbc',
      'ctr',
      'encryption',
      'decryption',
      'symmetric',
      'pbkdf2',
      'hmac',
      'mac',
      'sha256',
      'web crypto',
      'nonce',
      'iv',
    ],
    load: () => import('./aes.js').then((m) => m.initAes()),
  },
  {
    id: 'rsa',
    label: 'RSA',
    group: 'crypto',
    summary: 'Key generation, encryption and attacks on weak keys',
    tags: ['rsa', 'public key', 'private key', 'encryption', 'asymmetric', 'cryptography', 'factoring', 'wiener', 'hastad', 'håstad', 'common modulus', 'attack'],
    load: () => import('./rsa.js').then((m) => m.initRSA()),
  },
  {
    id: 'diffiehellman',
    label: 'Diffie–Hellman',
    group: 'crypto',
    summary: 'Key exchange, step by step',
    tags: ['diffie', 'hellman', 'key exchange', 'shared secret', 'asymmetric', 'cryptography', 'dh'],
    load: () => import('./diffiehellman.js').then((m) => m.initDiffieHellman()),
  },
  {
    id: 'shamir',
    label: 'Shamir’s Secret Sharing',
    group: 'crypto',
    summary: 'A secret split into shares, any k of n to rebuild it',
    tags: [
      'shamir',
      'secret sharing',
      'shamir secret sharing',
      'sss',
      'threshold scheme',
      'k of n',
      'slip-0039',
      'slip39',
      'trezor',
      'shamir backup',
      'mnemonic',
      'seed phrase',
      'lagrange interpolation',
      'polynomial',
      'gf(256)',
      'split a secret',
    ],
    load: () => import('./shamir.js').then((m) => m.initShamir()),
  },
  {
    id: 'timelock',
    label: 'Time-Lock Puzzle',
    group: 'crypto',
    summary: 'A message locked behind squarings no computer can hurry',
    tags: [
      'time-lock puzzle',
      'timelock',
      'time lock',
      'timed-release crypto',
      'time capsule',
      'lcs35',
      'rivest',
      'shamir',
      'wagner',
      'repeated squaring',
      'sequential',
      'verifiable delay',
      'vdf',
      'fabrot',
    ],
    load: () => import('./timelock.js').then((m) => m.initTimeLock()),
  },
  {
    id: 'quantum',
    label: 'Quantum Computer',
    group: 'crypto',
    summary: 'Shor’s factoring and Grover’s search on a simulated register',
    tags: ['quantum computer', 'quantum', 'qubit', 'shor', 'shor algorithm', 'grover', 'grover search', 'factoring', 'period finding', 'quantum fourier transform', 'qft', 'superposition', 'post-quantum'],
    load: () => import('./quantum.js').then((m) => m.initQuantum()),
  },
  {
    id: 'mlkem',
    label: 'ML-KEM',
    group: 'crypto',
    summary: 'Post-quantum encryption of a message, FIPS 203',
    textInput: true,
    primaryInput: '#mlk-message',
    tags: [
      'ml-kem',
      'kyber',
      'crystals-kyber',
      'post-quantum',
      'quantum',
      'quantum-safe',
      'quantum resistant',
      'pqc',
      'lattice',
      'module-lwe',
      'learning with errors',
      'fips 203',
      'key encapsulation',
      'kem',
      'aes-256-gcm',
      'encryption',
      'public key',
    ],
    load: () => import('./mlkem.js').then((m) => m.initMlkem()),
  },
  {
    id: 'qkd',
    label: 'BB84',
    group: 'crypto',
    summary: 'Quantum key distribution, simulated: Eve shows in the errors',
    textInput: true,
    primaryInput: '#qkd-message',
    tags: [
      'bb84',
      'quantum key distribution',
      'qkd',
      'quantum cryptography',
      'quantum',
      'photon',
      'polarization',
      'polarisation',
      'bennett',
      'brassard',
      'eavesdropper',
      'eve',
      'one-time pad',
    ],
    load: () => import('./qkd.js').then((m) => m.initQkd()),
  },
  {
    id: 'passwordgenerator',
    label: 'Passwords',
    group: 'crypto',
    summary: 'Generate passwords and analyse their strength',
    textInput: true,
    handlesPrefill: true,
    tags: [
      'password',
      'password generator',
      'password analyzer',
      'generate',
      'random',
      'passphrase',
      'diceware',
      'strength',
      'entropy',
      'crack time',
      'secure',
    ],
    load: () => import('./passwordgenerator.js').then((m) => m.initPasswordGenerator()),
  },

  // ── Code — Text ──
  {
    id: 'pattern',
    label: 'Word Finder',
    group: 'text',
    summary: 'Dictionary search by letter pattern',
    textInput: true,
    tags: ['pattern match', 'dictionary', 'substitute', 'solve', 'wildcard', 'search', 'crossword'],
    load: () => import('./pattern.js').then((m) => m.initPattern()),
  },
  {
    id: 'wordsplit',
    label: 'Word Splitter',
    group: 'text',
    summary: 'Insert word breaks into unspaced text',
    textInput: true,
    tags: ['word segmentation', 'insert spaces', 'unspaced', 'word breaks', 'plaintext', 'spacing', 'split words'],
    load: () => import('./wordsplit.js').then((m) => m.initWordSplit()),
  },
  {
    id: 'regexlab',
    label: 'Regex Lab',
    group: 'text',
    summary: 'Regular expressions with live matches',
    textInput: true,
    tags: ['regex', 'regular expression', 'pattern', 'match', 'replace', 'test', 'extract'],
    load: () => import('./regexlab.js').then((m) => m.initRegexLab()),
  },
  {
    id: 'diff',
    label: 'Diff',
    group: 'text',
    summary: 'Compare two texts',
    textInput: true,
    primaryInput: '#dif-input-a',
    tags: ['diff', 'compare', 'difference', 'similarity', 'edit distance', 'side by side'],
    load: () => import('./diff.js').then((m) => m.initDiff()),
  },

  // ── Code — Archive ──
  {
    id: 'archive',
    label: 'Cipher Archive',
    group: 'archive',
    summary: 'Historical and unsolved ciphertexts, with their sources',
    tags: [
      'historical ciphers',
      'unsolved',
      'great cipher',
      'grand chiffre',
      'louis xiv',
      'iron mask',
      'kryptos',
      'beale',
      'gold-bug',
      'poe',
      'enigma message',
      'barbarossa',
      'pt-109',
      'kennedy',
      'adfgvx',
      'painvin',
      'vicksburg',
      'civil war',
      "d'agapeyeff",
      'famous ciphers',
    ],
    load: () => import('./archive.js').then((m) => m.initArchive()),
  },

  // ── Occult — Astrology ──
  {
    id: 'natal',
    label: 'Natal Chart',
    group: 'astrology',
    summary: 'Chart wheel with Whole Sign houses',
    handlesPrefill: true,
    tags: ['natal', 'birth chart', 'chart wheel', 'ascendant', 'midheaven', 'whole sign', 'houses', 'planets', 'uranus', 'neptune', 'pluto', 'horoscope', 'temperament', 'complexion', 'humours', 'sanguine', 'choleric', 'melancholic', 'phlegmatic'],
    load: () => import('./natal.js').then((m) => m.initNatal()),
  },
  {
    id: 'transits',
    label: 'Transits & Synastry',
    group: 'astrology',
    summary: 'Transits to a natal chart, and two charts compared',
    handlesPrefill: true,
    tags: [
      'transit',
      'transits',
      'synastry',
      'bi-wheel',
      'compatibility',
      'relationship',
      'inter-aspects',
      'cross-aspects',
      'house overlays',
      'applying',
      'separating',
      'saturn return',
      'whole sign',
    ],
    load: () => import('./transits.js').then((m) => m.initTransits()),
  },
  {
    id: 'progressions',
    label: 'Progressions',
    group: 'astrology',
    summary: 'Progressions, solar arc, primary directions, returns',
    handlesPrefill: true,
    tags: ['progressions', 'secondary progressions', 'progressed', 'solar arc', 'directions', 'solar return', 'lunar return', 'returns', 'predictive', 'forecast', 'progressed moon', 'naibod', 'primary directions', 'placidus', 'semi-arc', 'mundane aspects', 'converse directions'],
    load: () => import('./progressions.js').then((m) => m.initProgressions()),
  },
  {
    id: 'astronomy',
    label: 'Ephemeris',
    group: 'astrology',
    summary: 'Positions, ingresses, retrogrades and eclipses',
    handlesPrefill: true,
    tags: [
      'astronomy',
      'planets',
      'retrograde',
      'mercury retrograde',
      'eclipse',
      'transit',
      'aspect',
      'solstice',
      'equinox',
      'planetary position',
      'timeline',
      'ephemeris',
      'ingress',
      'station',
      'new moon',
      'full moon',
      'lunar phases',
      'void of course',
    ],
    load: () => import('./astronomy.js').then((m) => m.initAstronomy()),
  },
  {
    id: 'planethours',
    label: 'Planetary Hours',
    group: 'astrology',
    summary: 'Chaldean hours and the Heptameron’s angels',
    handlesPrefill: true,
    tags: ['planetary', 'chaldean', 'hour', 'day ruler', 'electional', 'sunrise', 'sunset', 'heptameron', 'angels', 'peter de abano', 'michael', 'gabriel', 'raphael', 'samael', 'sachiel', 'anael', 'cassiel'],
    load: () => import('./planethours.js').then((m) => m.initPlanetaryHours()),
  },
  {
    id: 'electional',
    label: 'Electional Search',
    group: 'astrology',
    summary: 'Times when Moon, planet and hour conditions hold',
    handlesPrefill: true,
    tags: ['electional', 'election', 'timing', 'void of course', 'moon sign', 'waxing', 'waning', 'retrograde', 'planetary hour', 'best time'],
    load: () => import('./electional.js').then((m) => m.initElectional()),
  },
  {
    id: 'horary',
    label: 'Horary',
    group: 'astrology',
    summary: 'A question’s chart judged as Lilly taught it',
    tags: ['horary', 'lilly', 'christian astrology', 'question', 'querent', 'quesited', 'significator', 'regiomontanus', 'fortitudes', 'debilities', 'reception', 'translation of light', 'void of course', 'square chart'],
    load: () => import('./horary.js').then((m) => m.initHorary()),
  },
  {
    id: 'hellenistic',
    label: 'Hellenistic Astrology',
    group: 'astrology',
    summary: 'Dignities, lots, profections, releasing, hyleg',
    handlesPrefill: true,
    // Field ids before 2026-10-19 (a date and Ascendant per tab), read by older links and sessions
    renamedFields: {
      'hel-dig-date': 'hel-date',
      'hel-prof-birth': 'hel-date',
      'hel-lots-asc': 'hel-asc-sign',
      'hel-prof-asc': 'hel-asc-sign',
      'hel-lots-deg': 'hel-asc-deg',
    },
    tags: [
      'dignity',
      'essential dignity',
      'domicile',
      'exaltation',
      'triplicity',
      'terms',
      'bounds',
      'decan',
      'face',
      'sect',
      'lots',
      'fortune',
      'spirit',
      'profection',
      'traditional astrology',
      'hellenistic',
      'hayz',
      'cazimi',
      'combust',
      'hyleg',
      'prorogator',
      'alcocoden',
      'kadkhuda',
      'almuten',
      'twelfth-parts',
      'dodecatemoria',
    ],
    load: () => import('./hellenistic.js').then((m) => m.initHellenistic()),
  },
  {
    id: 'vedic',
    label: 'Vedic Chart',
    group: 'astrology',
    summary: 'Sidereal chart, nakṣatras, daśās, Pañcāṅga and the breath',
    handlesPrefill: true,
    tags: ['vedic', 'jyotish', 'jyotisha', 'sidereal', 'lahiri', 'ayanamsa', 'nakshatra', 'nakṣatra', 'rasi', 'navamsa', 'dasha', 'vimshottari', 'panchanga', 'panchang', 'tithi', 'rahu', 'ketu', 'hindu astrology', 'indian astrology', 'svarodaya', 'swara', 'svara', 'science of breath', 'tattva', 'tattwa', 'nadi', 'prithivi', 'akasha', 'rama prasad'],
    load: () => import('./vedic.js').then((m) => m.initVedic()),
  },
  {
    id: 'bazi',
    label: 'Four Pillars',
    group: 'astrology',
    summary: 'Bazi: year, month, day and hour as stems and branches',
    handlesPrefill: true,
    tags: ['bazi', 'ba zi', 'four pillars', '八字', '四柱', 'chinese astrology', 'heavenly stems', 'earthly branches', 'ganzhi', 'ten gods', 'day master', 'luck pillars', 'da yun', 'nayin', 'solar terms', 'jieqi', 'lichun', 'zi ping'],
    load: () => import('./bazi.js').then((m) => m.initBazi()),
  },
  {
    id: 'fixedstars',
    label: 'Fixed Stars',
    group: 'astrology',
    summary: 'Star places, chart stars, parans, heliacal risings',
    handlesPrefill: true,
    tags: ['fixed stars', 'stars', 'behenian', 'agrippa', 'ptolemy', 'regulus', 'algol', 'spica', 'aldebaran', 'antares', 'sirius', 'royal stars', 'precession', 'star conjunction', 'heliacal rising', 'heliacal setting', 'acronychal rising', 'cosmical setting', 'arcus visionis', 'first visibility', 'hesiod', 'pleiades', 'paran', 'parans', 'co-rising', 'culmination'],
    load: () => import('./fixedstars.js').then((m) => m.initFixedStars()),
  },
  {
    id: 'mansions',
    label: 'Lunar Mansions',
    group: 'astrology',
    summary: 'The Moon’s 28 mansions, after Agrippa and the Picatrix',
    tags: ['lunar mansions', 'mansions of the moon', 'manazil', 'manāzil', 'picatrix', 'agrippa', 'moon', 'talisman', 'images', 'electional', 'alnath', 'arabic astrology'],
    load: () => import('./mansions.js').then((m) => m.initMansions()),
  },
  {
    id: 'astrocartography',
    label: 'Astrocartography',
    group: 'astrology',
    summary: 'The planets’ lines on the Earth, and Ptolemy’s nations',
    handlesPrefill: true,
    tags: ['astrocartography', 'astro*carto*graphy', 'locational astrology', 'relocation', 'planetary lines', 'world map', 'map', 'jim lewis', 'midheaven line', 'ascendant line', 'crossings', 'parans', 'chorography', 'ptolemy', 'tetrabiblos', 'nations', 'countries', 'triplicities', 'mundane astrology', 'geography'],
    load: () => import('./astrocartography.js').then((m) => m.initAstrocartography()),
  },
  {
    id: 'zodiac',
    label: 'Zodiac',
    group: 'astrology',
    summary: 'The Sun’s sign, decan and term, and the Chinese sign',
    handlesPrefill: true,
    tags: [
      'astrology',
      'star sign',
      'sun sign',
      'cusp',
      'decan',
      'face',
      'term',
      'bound',
      'dignity',
      'chinese zodiac',
    ],
    load: () => import('./zodiac.js').then((m) => m.initZodiac()),
  },
  {
    id: 'gauquelin',
    label: 'Gauquelin Sectors',
    group: 'astrology',
    summary: 'Diurnal sectors, the Mars and Moon effects, against chance',
    handlesPrefill: true,
    tags: ['gauquelin', 'mars effect', 'moon effect', 'sectors', 'diurnal circle', 'rising', 'culmination', 'sports champions', 'writers', 'müller', 'comité para', 'zelen', 'csicop', 'cfepp', 'ertel', 'eminence', 'statistics', 'chance', 'control group'],
    load: () => import('./gauquelin.js').then((m) => m.initGauquelin()),
  },
  {
    id: 'harmony',
    label: 'Harmony of the Spheres',
    group: 'astrology',
    summary: 'Kepler’s planetary intervals of 1619, recomputed and played',
    tags: [
      'harmony of the spheres',
      'music of the spheres',
      'kepler',
      'harmonices mundi',
      'harmonic law',
      'third law',
      'planetary intervals',
      'aphelion',
      'perihelion',
      'mi fa mi',
      'planets',
      'music',
    ],
    load: () => import('./harmony.js').then((m) => m.initHarmony()),
  },

  // ── Occult — Gematria & numerology ──
  {
    id: 'gematria',
    label: 'Gematria',
    group: 'numbers',
    summary: 'Hebrew, English and custom ciphers',
    textInput: true,
    handlesPrefill: true,
    tags: ['hebrew', 'value', 'word', 'letter value', 'mispar', 'strong', 'lexicon', 'tanakh', 'torah', 'bible verse', 'kidmi', 'milui', 'filling', 'neelam', 'kolel', 'ribua', 'pardes rimonim', 'cordovero', 'mispar shemi', 'square value', 'agrippa', 'arithmancy', 'jewish cipher'],
    load: () => import('./gematria.js').then((m) => m.initGematria()),
  },
  {
    id: 'nexus',
    label: 'Gematria Nexus',
    group: 'numbers',
    summary: 'A name, word, number or date across every system',
    textInput: true,
    tags: [
      'name and date profile',
      'birth date',
      'profile',
      'tarot birth card',
      'synchronicity',
      'convergence',
      'gematria nexus',
      'gematria',
      'connection',
      'cross-cipher',
      'compare',
      'numerology',
      'zodiac',
      'tarot',
    ],
    load: () => import('./nexus.js').then((m) => m.initNexus()),
  },
  {
    id: 'isopsephy',
    label: 'Isopsephy & Abjad',
    group: 'numbers',
    summary: 'Greek isopsephy and numerals, Arabic abjad',
    textInput: true,
    tags: [
      'isopsephy',
      'greek',
      'greek numerals',
      'milesian',
      'keraia',
      'abjad',
      'arabic',
      'hisab al-jummal',
      'mashriqi',
      'maghribi',
      'chronogram',
      'strong',
      'lexicon',
      'new testament',
      'persian',
      'abrasax',
      '888',
      '786',
      'letter values',
    ],
    load: () => import('./isopsephy.js').then((m) => m.initIsopsephy()),
  },
  {
    id: 'numerochart',
    label: 'Numerology Chart',
    group: 'numbers',
    summary: 'Core numbers, cycles, pinnacles and challenges',
    textInput: true,
    handlesPrefill: true,
    tags: [
      'life path',
      'expression',
      'soul urge',
      'personality',
      'pinnacles',
      'challenges',
      'chart',
      'full name',
      'birth',
    ],
    load: () => import('./numerochart.js').then((m) => m.initNumeroChart()),
  },
  {
    id: 'numerology',
    label: 'Root Numbers',
    group: 'numbers',
    summary: 'Reduction and master numbers',
    textInput: true,
    handlesPrefill: true,
    tags: ['root', 'digit', 'reduce', 'number meaning', 'master number'],
    load: () => import('./numerology.js').then((m) => m.initNumerology()),
  },
  {
    id: 'fibonacci',
    label: 'Fibonacci & Primes',
    group: 'numbers',
    summary: 'Sequences, primes and factors',
    tags: ['prime', 'fibonacci', 'factor', 'sequence', 'number theory', 'sacred', 'golden ratio'],
    load: () => import('./fibonacci.js').then((m) => m.initFibonacci()),
  },

  // ── Occult — Divination ──
  {
    id: 'tarot',
    label: 'Tarot',
    group: 'divination',
    summary: 'The 78 cards of 1909, in Waite’s and Book T’s words',
    tags: ['cards', 'arcana', 'divination', 'reading', 'spread', 'major', 'minor', 'rider waite', 'waite', 'pamela colman smith', 'celtic cross', 'significator', 'pictorial key', 'golden dawn', 'book t', 'opening of the key', 'equinox'],
    load: () => import('./tarot.js').then((m) => m.initTarot()),
  },
  {
    id: 'iching',
    label: 'I Ching',
    group: 'divination',
    summary: 'The 64 hexagrams, cast and read',
    tags: ['hexagram', 'trigram', 'oracle', 'divination', 'chinese', 'yijing', 'zhouyi', 'book of changes', 'yarrow', 'coins', 'legge', 'zhu xi', 'shuo kua', 'nuclear hexagram', 'hugua', 'fu xi', 'shao yong', 'leibniz', 'binary', 'wu cheng'],
    load: () => import('./iching.js').then((m) => m.initIChing()),
  },
  {
    id: 'geomancy',
    label: 'Geomancy',
    group: 'divination',
    summary: 'Figures and the shield chart',
    tags: [
      'geomantic',
      'divination',
      'figures',
      'shield chart',
      'oracle',
      'earth divination',
      'dots',
      'mothers',
      'ilm al-raml',
    ],
    load: () => import('./geomancy.js').then((m) => m.initGeomancy()),
  },
  {
    id: 'lotbooks',
    label: 'Lot Books',
    group: 'divination',
    summary: 'Books of fortune run by dice and number, as printed',
    tags: ['lot book', 'lot-book', 'sortes', 'book of fate', 'book of fortune', 'napoleon', 'dodechedron', 'chance of the dice', 'dice', 'wheel of pythagoras', 'pythagoras wheel', 'onomancy', 'spirito', 'libro della ventura', 'fortune telling', 'cleromancy', 'astragalomancy', 'oracle'],
    load: () => import('./lotbooks.js').then((m) => m.initLotBooks()),
  },

  // ── Occult — Kabbalah ──
  {
    id: 'kabbalah',
    label: 'Tree of Life',
    group: 'kabbalah',
    summary: 'The ten sefirot and the 22 paths',
    tags: ['kabbalah', 'qabalah', 'tree of life', 'sephirot', 'sefirot', 'hebrew', 'jewish', 'paths'],
    load: () => import('./kabbalah.js').then((m) => m.initKabbalah()),
  },
  {
    id: 'atlas',
    label: 'Correspondence Atlas',
    group: 'kabbalah',
    summary: 'The correspondences as a graph, each link with its source',
    handlesPrefill: true,
    tags: ['correspondence', 'correspondences', 'golden dawn', '777', 'book t', 'sefirot', 'planets', 'zodiac', 'elements', 'hebrew', 'tarot', 'paths', 'decans', 'faces', 'court cards', 'angels', 'shem ha-mephorash', 'lunar mansions', 'fixed stars', 'behenian', 'geomantic figures', 'traditions', 'knowledge graph'],
    load: () => import('./atlas.js').then((m) => m.initAtlas()),
  },
  {
    id: 'temurah',
    label: 'Temurah & Notarikon',
    group: 'kabbalah',
    summary: 'Letter exchanges, Aiq Bekar and acronyms on Hebrew',
    textInput: true,
    tags: [
      'temurah',
      'notarikon',
      'notariqon',
      'rashei tevot',
      'acronym',
      'atbash',
      'albam',
      'atbah',
      'avgad',
      'aiq bekar',
      'nine chambers',
      'letter exchange',
      'permutation',
      'hebrew cipher',
      'kabbalah',
    ],
    load: () => import('./temurah.js').then((m) => m.initTemurah()),
  },
  {
    id: 'els',
    label: 'ELS Search',
    group: 'kabbalah',
    summary: 'Equidistant letter sequences in the Tanakh, against chance',
    textInput: true,
    handlesPrefill: true,
    primaryInput: '#els-word',
    tags: ['els', 'bible code', 'bible codes', 'torah code', 'equidistant letter sequence', 'skip', 'witztum', 'rips', 'drosnin', 'mckay', 'tanakh', 'hidden text'],
    load: () => import('./els.js').then((m) => m.initEls()),
  },
  {
    id: 'gates',
    label: 'The 231 Gates',
    group: 'kabbalah',
    summary: 'Sefer Yetzirah’s wheel of the 22 letters',
    textInput: true,
    primaryInput: '#gat-word',
    tags: ['231 gates', 'sefer yetzirah', 'book of formation', 'wheel', 'letter combination', 'tzeruf', 'abulafia', 'mothers', 'doubles', 'simples', 'permutation', 'hebrew', 'kabbalah'],
    load: () => import('./gates.js').then((m) => m.initGates()),
  },
  {
    id: 'shemhamephorash',
    label: 'The 72 Names',
    group: 'kabbalah',
    summary: 'The Shem ha-Mephorash from Exodus 14:19–21',
    tags: ['72 names', 'shem hamephorash', 'shem ha-mephorash', 'schemhamphorasch', 'names of god', 'exodus 14', 'angels', 'agrippa', 'goetia', 'kabbalah', 'hebrew'],
    load: () => import('./shemhamephorash.js').then((m) => m.initShemhamephorash()),
  },

  // ── Occult — Symbols & scripts ──
  {
    id: 'symbolicscripts',
    label: 'Symbolic Scripts',
    group: 'symbols',
    summary: 'Runes, Ogham, Enochian and the magical alphabets',
    textInput: true,
    tags: [
      'runes',
      'ogham',
      'enochian',
      'elder futhark',
      'celtic',
      'angelic',
      'script',
      'ancient',
      'john dee',
      'theban',
      'witches alphabet',
      'honorius',
      'celestial alphabet',
      'malachim',
      'passing the river',
      'transitus fluvii',
      'magical alphabet',
      'agrippa',
      'trithemius',
      'ambrogio',
      'raphael',
      'apollonius',
      'hildegard',
      'lingua ignota',
      'litterae ignotae',
      'ibn wahshiyya',
      'hammer',
      'arabic',
      'planetary alphabets',
      'zodiac alphabets',
    ],
    // The scripts were a row of buttons before 2026-10-25, now one list with the same values
    renamedFields: { 'ssc-scripts': 'ssc-script' },
    load: () => import('./symbolicscripts.js').then((m) => m.initSymbolicScripts()),
  },
  {
    id: 'enochian',
    label: 'Enochian Tables',
    group: 'symbols',
    summary: 'Dee and Kelley’s Great Table and its names',
    tags: ['enochian', 'john dee', 'edward kelley', 'great table', 'watchtowers', 'black cross', 'tablet of union', 'angels', 'seniors', 'bataiva', 'true and faithful relation', 'golden dawn', 'cipher'],
    load: () => import('./enochian.js').then((m) => m.initEnochian()),
  },
  {
    id: 'soyga',
    label: 'Book of Soyga',
    group: 'symbols',
    summary: 'The 36 letter tables of Aldaraia, made by rule',
    tags: ['soyga', 'aldaraia', 'book of soyga', 'john dee', 'letter tables', 'magic tables', 'jim reeds', 'sloane 8', 'bodley 908', 'liber loagaeth', 'moyses', 'tabula magistri'],
    load: () => import('./soyga.js').then((m) => m.initSoyga()),
  },
  {
    id: 'llull',
    label: 'Ars Brevis',
    group: 'symbols',
    summary: 'Ramon Llull’s alphabet, four figures and Table',
    tags: ['llull', 'lull', 'lullian art', 'ramon llull', 'raymond lully', 'ars brevis', 'ars magna', 'ars generalis ultima', 'combinatorial', 'ars combinatoria', 'figures', 'volvelle', 'leibniz', 'bruno'],
    load: () => import('./llull.js').then((m) => m.initLlull()),
  },
  {
    id: 'symbolatlas',
    label: 'Symbology',
    group: 'symbols',
    summary: 'Alchemical, Masonic, Hermetic and other symbols',
    tags: [
      'alchemy',
      'freemasonry',
      'hermetic',
      'egyptian',
      'esoteric',
      'symbol',
      'pentagram',
      'hexagram',
      'ankh',
      'ouroboros',
      'caduceus',
      'rosicrucian',
    ],
    load: () => import('./symbolatlas.js').then((m) => m.initSymbolAtlas()),
  },
  {
    id: 'sacredgeometry',
    label: 'Sacred Geometry',
    group: 'symbols',
    summary: 'Constructions and the Platonic solids',
    tags: [
      'flower of life',
      'metatron',
      'vesica piscis',
      'golden ratio',
      'fibonacci',
      'sri yantra',
      'platonic solids',
      'sacred',
      'geometry',
    ],
    load: () => import('./sacredgeometry.js').then((m) => m.initSacredGeometry()),
  },
  {
    id: 'sigil',
    label: 'Sigil',
    group: 'symbols',
    summary: 'Sigils from statements of intent',
    textInput: true,
    tags: ['chaos magic', 'intention', 'glyph', 'symbol', 'sigil', 'austin osman spare'],
    load: () => import('./sigil.js').then((m) => m.initSigil()),
  },

  // ── Occult — Alchemy & Hermetica ──
  {
    id: 'alchemicalsigns',
    label: 'Alchemical Signs',
    group: 'alchemy',
    summary: 'The Oraculum’s signs of 1755, traced and looked up',
    tags: [
      'alchemical signs',
      'alchemical symbols',
      'alchemy',
      'chemical signs',
      'apothecary',
      'pharmacy',
      'recipe',
      'oraculum',
      'zeichen',
      'mercury',
      'sulphur',
      'salt',
      'vitriol',
      'antimony',
      'metals',
    ],
    load: () => import('./alchemicalsigns.js').then((m) => m.initAlchemicalSigns()),
  },
  {
    id: 'emeraldtablet',
    label: 'Emerald Tablet',
    group: 'alchemy',
    summary: 'The Tabula Smaragdina in its versions, sentence by sentence',
    tags: [
      'emerald tablet',
      'tabula smaragdina',
      'smaragdine table',
      'hermes trismegistus',
      'hermetic',
      'as above so below',
      'balinas',
      'apollonius of tyana',
      'secret of creation',
      'secretum secretorum',
      'hortulanus',
      'newton',
      'ruska',
      'alchemy',
      'arabic',
    ],
    load: () => import('./emeraldtablet.js').then((m) => m.initEmeraldTablet()),
  },
  {
    id: 'atalanta',
    label: 'Atalanta Fugiens',
    group: 'alchemy',
    summary: 'Maier’s fifty emblems of 1618 and their fugues, played',
    tags: [
      'atalanta fugiens',
      'michael maier',
      'maier',
      'emblem',
      'emblem book',
      'fugue',
      'canon',
      'music',
      'epigram',
      'engraving',
      'de bry',
      'alchemy',
      'hippomenes',
    ],
    load: () => import('./atalanta.js').then((m) => m.initAtalanta()),
  },
  {
    id: 'chymicalwedding',
    label: 'The Chymical Wedding',
    group: 'alchemy',
    summary: 'The name riddle of 1616, every numbering tried',
    tags: [
      'chymical wedding',
      'chemical wedding',
      'chymische hochzeit',
      'christian rosenkreutz',
      'rosencreutz',
      'rosicrucian',
      'andreae',
      'foxcroft',
      'riddle',
      'name riddle',
      'alchimia',
      'alchemy',
    ],
    load: () => import('./chymicalwedding.js').then((m) => m.initChymicalWedding()),
  },
  {
    id: 'monas',
    label: 'Monas Hieroglyphica',
    group: 'alchemy',
    summary: 'Dee’s sign built theorem by theorem from the print of 1564',
    tags: [
      'monas hieroglyphica',
      'hieroglyphic monad',
      'monad',
      'john dee',
      'dee',
      'theorem',
      'construction',
      'sun',
      'moon',
      'cross',
      'aries',
      'alchemy',
      'hermetic',
    ],
    load: () => import('./monas.js').then((m) => m.initMonas()),
  },
  {
    id: 'magicsquare',
    label: 'Magic Squares',
    group: 'alchemy',
    summary: 'The seven planetary kameas',
    tags: ['grid', 'sum', 'mathematical', 'planetary', 'kamea', 'agrippa', 'lo shu'],
    load: () => import('./magicsquare.js').then((m) => m.initMagicSquare()),
  },
  {
    id: 'kameasigil',
    label: 'Kamea Sigils',
    group: 'alchemy',
    summary: 'Names traced on the seven planetary squares',
    textInput: true,
    tags: [
      'kamea',
      'planetary square',
      'magic square',
      'sigil',
      'seal',
      'agrippa',
      'aiq bekar',
      'nine chambers',
      'hebrew',
      'intelligence',
      'spirit',
      'golden dawn',
    ],
    load: () => import('./kameasigil.js').then((m) => m.initKameaSigil()),
  },

  // ── Occult — Calendars ──
  {
    id: 'calendar',
    label: 'Calendar Systems',
    group: 'calendars',
    summary: 'Sixteen calendars, Masonic eras, Easter and the molad',
    handlesPrefill: true,
    tags: [
      'lunar',
      'chinese',
      'gregorian',
      'julian',
      'hebrew',
      'maya',
      'hijri',
      'islamic',
      'persian',
      'jalali',
      'anno lucis',
      'anno mundi',
      'roman',
      'kalends',
      'ides',
      'ab urbe condita',
      'masonic',
      'freemasonry',
      'royal arch',
      'knights templar',
      'umm al-qura',
      'coptic',
      'ethiopian',
      'indian national',
      'saka',
      'dangi',
      'korean',
      'japanese era',
      'reiwa',
      'buddhist era',
      'minguo',
      'julian day',
      'iso week',
      'date converter',
      'sabbat',
      'solstice',
      'equinox',
      'ritual',
      'easter',
      'computus',
      'golden number',
      'epact',
      'sunday letter',
      'dominical letter',
      'paschal full moon',
      'orthodox easter',
      'clavius',
      'molad',
      'rosh hashanah',
      'dehiyyot',
      'tekufah',
      'tekufot',
      'maimonides',
    ],
    load: () => import('./calendar.js').then((m) => m.initCalendar()),
  },
  {
    id: 'dateresonance',
    label: 'Date Resonance',
    group: 'calendars',
    summary: 'The symbolic profile of a date',
    tags: ['symbolic', 'numerology', 'date', 'moon', 'zodiac', 'resonance', 'hebrew', 'maya', 'birthday'],
    load: () => import('./dateresonance.js').then((m) => m.initDateResonance()),
  },
  {
    id: 'duration',
    label: 'Time Tools',
    group: 'calendars',
    summary: 'Durations, clock mirror and opposite, time zones',
    handlesPrefill: true,
    tags: [
      'days',
      'between',
      'difference',
      'dates',
      'countdown',
      'mirror',
      'opposite',
      'reverse',
      'time',
      'clock',
      'time zone',
      'timezone',
      'utc',
      'gmt',
      'daylight saving',
      'dst',
      'world clock',
      'iana',
    ],
    load: () => import('./duration.js').then((m) => m.initDuration()),
  },
];

// ─── Derived lookups ─────────────────────────────────────────────────────────

export const toolById = Object.fromEntries(TOOLS.map((t) => [t.id, t]));
export const groupById = Object.fromEntries(GROUPS.map((g) => [g.id, g]));
export const roomById = Object.fromEntries(ROOMS.map((r) => [r.id, r]));

// ─── Retired URLs ────────────────────────────────────────────────────────────
// A tool merged into another keeps its address: the old id opens the tool that
// absorbed it (the router in main.js, a 301 in dist/_redirects), `fields` maps
// the old input ids onto the new ones so share links and saved sessions still fill
// it, and `state` sets the tab the old tool became (applied first, so shared
// fields land in it). An alias is never reused as a tool id.

export const TOOL_ALIASES = {
  // Name & Date Profile, merged into Gematria Nexus on 2026-10-18
  oracle: { tool: 'nexus', fields: { 'orc-name': 'nxs-text', 'orc-date': 'nxs-date' } },
  // Signal Codes (NATO alphabet and tap code), merged into Puzzle Codes on 2026-10-18
  signalcodes: { tool: 'puzzlecodes', fields: { 'sig-input': 'pzc-input' }, state: { 'pzc-system': 'nato' } },
  // Password Analyzer, merged into Passwords on 2026-10-18 (its password was never saved)
  passwordanalyzer: { tool: 'passwordgenerator', state: { 'pw-tabs': 'analyze' } },
  // Word Counter, merged into Frequency Analysis (its Counts tab) on 2026-10-18
  wordcounter: { tool: 'cryptanalysis', fields: { 'wordcount-input': 'cry-input' }, state: { 'cry-tabs': 'counts' } },
  // Cipher Pipeline, replaced by Cipher Chain on 2026-10-30 (its steps were never saved; its text is the message)
  pipeline: { tool: 'chain', fields: { 'pipe-input': 'chn-message' }, state: { 'chn-tabs': 'encode' } },
};

/** The id a tool is reached by today: an alias resolves to the tool that absorbed it. */
export const resolveToolId = (toolId) => TOOL_ALIASES[toolId]?.tool ?? toolId;

// ─── Pages ───────────────────────────────────────────────────────────────────
// The site's pages that are not tools, at /{pageId} beside them (the router in
// main.js), each with a static page and a sitemap entry from the build like a
// tool's. `render` gives the page's HTML from data alone, in the app and the
// build alike. A page id is never a tool's, an alias's or a side's.

export const PAGES = [
  {
    id: 'sources',
    label: 'Sources & method',
    description:
      'Where Unveil’s texts, tables and calculations come from: the works each world’s tools cite, with links to the scans; how texts and calculations are checked; what is later tradition, and what was left out and why.',
    render: () => import('./utils/sources-page.js').then((m) => m.sourcesPageHtml()),
  },
  {
    id: 'evidence',
    label: 'Chance & evidence',
    description:
      'How Unveil’s tools measure a hidden pattern against chance — expected counts, shuffled controls, looking everywhere, free choices — and the claims they test: Bible codes, Virgil’s and Aratus’s acrostics, the alphabetic psalms, Gallup’s Folio cipher, names that total 666, the Mars effect.',
    render: () => import('./utils/evidence-page.js').then((m) => m.evidencePageHtml()),
  },
  {
    id: 'glossary',
    label: 'Glossary',
    description:
      'The terms that recur across Unveil’s tools — index of coincidence, quadgrams, orb, sect, ayanāṃśa, gematria, the sefirot and more — each explained in a few sentences with its source and the tools that use it.',
    render: () => import('./utils/glossary.js').then((m) => m.glossaryPageHtml()),
  },
];

export const pageById = Object.fromEntries(PAGES.map((p) => [p.id, p]));

/** A tool's current name, for buttons that open it — renames then reach every button. */
export const toolLabel = (toolId) => toolById[toolId]?.label ?? toolId;

export function roomOfTool(toolId) {
  const tool = toolById[toolId];
  return tool ? groupById[tool.group].room : null;
}

export function groupsInRoom(roomId) {
  return GROUPS.filter((g) => g.room === roomId);
}

export function toolsInGroup(groupId) {
  return TOOLS.filter((t) => t.group === groupId);
}

export function toolsInRoom(roomId) {
  return TOOLS.filter((t) => groupById[t.group].room === roomId);
}

// ─── SEO: per-tool meta descriptions ────────────────────────────────────────
// Keyword-rich descriptions for dynamic <title> and <meta description> updates.

export const SEO_DESCRIPTIONS = {
  caesar:
    'Online Caesar cipher, ROT13, Atbash, and Affine cipher encoder/decoder. Shift, rotate, and transform text with classic substitution ciphers.',
  substitution:
    'Substitution cipher solver: simple substitution with word patterns, dictionary checks and interactive mapping, and homophonic ciphers like the Zodiac 408 solved by simulated annealing.',
  vigenere:
    'Vigenère cipher encoder/decoder with Beaufort, Autokey, Gronsfeld, and Porta variants. Includes key search with IC-based key length detection.',
  playfair:
    'Playfair and Four-Square cipher tool — encrypt and decrypt with classic 5×5 digraph ciphers, and solve Playfair without the key by simulated annealing.',
  enigma:
    'Enigma simulator — the Enigma I, M3 and M4, the commercial Enigma K, the Railway Enigma and the Abwehr’s Enigma G (G-312, G-260, G-228, G-111) — with the Bombe: break a three-rotor message from a crib, ten plugs and all, or from the ciphertext alone with few plugs — in the browser.',
  hagelin:
    'Hagelin M-209 cipher machine simulator and solver: recover pins and lugs from known plaintext or from the ciphertext alone; the key lists of TM 11-380 (1942, 1944) and a 1943 division’s, with their 26-letter checks.',
  lorenz:
    'Lorenz SZ40/42 (Tunny) simulator with Colossus: set all twelve wheel starts from the ciphertext by Tutte’s delta counts; the 2007 Cipher Event and Tiltman’s 1944 example, ITA2 teleprinter code.',
  purple:
    'PURPLE, the Japanese diplomatic cipher machine (Angooki Taipu B), as the U.S. Army SIS rebuilt it: the sixes and twenties switches of R.I.P. 77, the 14-part message of December 1941 and a Tokyo–Berlin message of 1942 to read, and a ciphertext-only break in seconds by coincidence counting.',
  sigaba:
    'SIGABA (ECM Mark II, CSP-889 and CSP-2900) simulator: cipher, control and index rotors as the key lists give them, the message setting from an indicator and the 26–30 check; the message setting or the index setting recovered from known plaintext by Lasry’s meet in the middle.',
  typex:
    'Typex, the British cipher machine, with the drums the U.S. Navy’s OP-20-G tabled from a Royal Navy Mark II in 1945: five drums, two of them stators, reversible inserts, the Mark 22’s stecker; the key found from a crib, as a Bombe would.',
  chaocipher:
    'Chaocipher: John F. Byrne’s two-wheel cipher as revealed in 2010, his keying of the alphabets, both wheels letter by letter, and the alphabets rebuilt from known plaintext by Cowan’s maze.',
  cylinder:
    'M-94 cipher cylinder (Jefferson disk): the US Army’s 25 disks from the 1948 history, key phrases to disk order as in FM 24-5, every row of the cylinder shown for deciphering.',
  vic:
    'VIC cipher: Reino Häyhänen’s Soviet hand cipher — key derivation by chain addition, straddling checkerboard, columnar and disrupted transpositions, keygroup — tested with his 1953 hollow-nickel message.',
  solitaire:
    'Solitaire (Pontifex): Bruce Schneier’s cipher for a deck of 52 cards and two jokers, from Cryptonomicon — keyed by a passphrase or any deck order, every pass over the deck shown step by step, tested with Schneier’s own test vectors.',
  transposition:
    'Rail Fence, Columnar, Route, Scytale and the turning grille, with solvers for rail fence, columnar and double transposition.',
  visualciphers:
    'Symbol ciphers from their prints — Porta’s grid, table of 400 pairs and stave cipher of 1563, Trithemius’s secret alphabets of 1518, the Freemasons’ cipher of 1745, the Templars’ alphabet, Poe’s Gold-Bug, the Dancing Men and the monks’ numerals — and the modern Pigpen: write in signs or read a cipher by tapping them; and Bacon’s biliteral cipher.',
  xor: 'XOR cipher and Vernam one-time pad tool for bitwise encryption. Supports hex, text key input, crib drag, and OTP key-length analysis.',
  bookcipher:
    'Book cipher (Ottendorf cipher) and code table: reference words by position in a key text, or read and write a nomenclator such as Louis XIV’s Great Cipher of 1691 with Bazeries’s table.',
  gridciphers:
    'Grid Ciphers — Bifid, Trifid, ADFGVX, Hill, the Nihilist cipher and the straddling checkerboard, both ways; Bifid, Trifid, ADFGVX and Hill also solved without the key, in the browser.',
  aca:
    'All 62 cipher types of the American Cryptogram Association — Amsco, Cadenus, Gromark, Nicodemus, Quagmires, Ragbaby, Phillips, Seriated Playfair, Morbit, Pollux, Fractionated Morse and the rest — enciphered and deciphered, every example on the ACA’s sheets checked, 45 of them solved without the key in the browser.',
  morse:
    'Morse code translator with audio playback. Convert text to dots and dashes with adjustable WPM and signal visualization.',
  converter:
    'Base converter for binary, octal, decimal, hex, Base64, Base32, Base58, Base85, URL encoding, and JWT decoding.',
  roman: 'Roman numeral converter. Convert between Roman numerals and decimal numbers instantly.',
  ascii: 'Unicode and ASCII character map. View code points, encodings, and character properties for any text.',
  zerowidth: 'Text steganography: hide a message in zero-width characters, in SNOW whitespace at line ends (compatible with SNOW, ICE password), in Cyrillic homoglyphs or as a Latin prayer with Trithemius’s Ave Maria tables (1518), and inspect any text for invisible characters, Unicode tags, variation selectors, bidi controls and lookalike letters.',
  lsb: 'Image steganography tool. Hide secret messages in image pixels using least significant bit encoding. Encode, decode, and analyze.',
  layers:
    'Layer Decoder — paste anything encoded and peel it layer by layer: Base64, Base32, hex, URL, Morse, ROT13, Caesar, one-byte XOR, gzip and more, until the text reads.',
  dnastego:
    'DNA steganography after Clelland, Risca and Bancroft (Nature, 1999): a message written three bases a symbol by their key, flanked by two PCR primers and hidden among fragments of other DNA, then found again by its primers and read; their own sequenced strand decoded to “JUNE6 INVASION:NORMANDY” by the key their patent prints whole, and the misprints of the key Nature printed.',
  visualcrypto:
    'Visual cryptography — Naor and Shamir’s scheme of 1994: a picture or a line of text split into transparencies of subpixels that show it only when k of the n are laid over one another, read by the eye alone; their extension that hides the secret in two innocent pictures; and the two dot patterns their paper encloses, which spell IACR.',
  trackingdots:
    'Printer tracking dots decoded — the 15 × 8 grid of faint yellow dots Xerox DocuColor colour laser printers put on every page, read by the rules EFF published in 2005: the printer’s serial number and the date and time of printing, checked by the grid’s parity, with a scan’s yellow made visible.',
  audiostego:
    'Audio Stego — see a recording’s spectrogram for pictures and words drawn into its frequencies, hide and find messages in the lowest bits of WAV samples, decode or make DTMF keypad tones, receive slow-scan television (SSTV) pictures in eighteen modes, and read Morse code from a recording at any speed.',
  fileforensics:
    'File Forensics — a file’s real type from its bytes, Exif and GPS metadata, data after its end, ZIP archives and files hidden inside, strings and entropy, all in the browser.',
  cipherid:
    'Automatic cipher identifier and solver. Detects 30+ types: Caesar, Vigenère, Playfair, Base64, Base32, Base58, Base85, MD5, SHA-256, bcrypt, JWT, Morse, and more.',
  cryptanalysis:
    'Frequency analysis for ciphertext — letter, bigram and trigram frequencies against 12 languages, entropy, index of coincidence with key-length estimate, Kasiski examination, autocorrelation, and counts of words, characters, letters, sentences and paragraphs.',
  unicity:
    'Shannon’s unicity distance (1949): how many letters of ciphertext fix the key, about H(K)/D, with each cipher’s keys counted — simple substitution, Vigenère, transposition, Playfair, Hill, Enigma — and the redundancy of English, German, French, Italian, Spanish and Latin measured over public-domain books as Shannon’s F_N. The app’s own solver shows random letters read as English below it, and Shannon’s guessing experiment of 1951 is played on his own sentences beside his subject’s guesses.',
  randomness:
    'NIST SP 800-22’s fifteen statistical tests of randomness — frequency, runs, rank, the discrete Fourier transform, templates, Maurer’s universal test, linear complexity, serial, approximate entropy, cumulative sums and random excursions — on pasted bits, hex or text, a file, the binary digits of e, π, √2 and √3 or a generator, each with its P-value; all 64 of the standard’s Appendix B values reproduced. And FIPS 140-1’s four power-up tests on 20,000 bits.',
  lindiff:
    'Linear and differential cryptanalysis replayed on Howard Heys’s teaching cipher, a 16-bit substitution–permutation network whose S-box is a row of DES’s: the linear approximation and difference distribution tables, his trail of bias −1/32 and his characteristic of probability 27/1024, and both attacks run in the browser, finding eight bits of the last subkey from known plaintexts or chosen pairs. His printed tables checked cell by cell, his erratum shown.',
  nomenclator:
    'Nomenclator solver for the code tables of the 1500s–1700s: signs for letters, syllables and words, nulls and code words — read with a key or broken without one in French, Latin, Italian, Spanish, German or English. Mary Stuart’s letters, the Copiale and the Borg cipher.',
  banburismus:
    'Banburismus — Alan Turing’s method against the naval Enigma, as Hut 8 ran it in 1940–43: Hugh Alexander’s worked example replayed step by step, and a simulated naval day broken the same way — messages set in depth, weighed in half-decibans, the right-hand wheel’s alphabet scritched and its turnover naming the wheel.',
  cyclometer:
    'Cyclometer — Marian Rejewski’s break of the Enigma, 1932–38, replayed: a day’s double-enciphered message keys, their characteristic, the catalogue of all 105,456 settings of rotors I–III, then the plugboard, the keys and the rings. The catalogue’s figures beside Alex Kuhl’s recount.',
  archive:
    'Cipher Archive — famous historical and unsolved ciphertexts with their sources: Mary Stuart’s letters of 1580, the Borg cipher, Louis XIV’s Great Cipher, the Copiale, Poe’s Gold-Bug, the Beale ciphers, the Radiogram of Victory, a 1941 Enigma message, the PT-109 Playfair messages and Kryptos. Open a solved one in the tool that reads it, key set.',
  chain:
    'Cipher Chain — run a message through ciphers, machines, encodings and hiding places in turn, from Caesar and the ACA’s cipher types to Enigma, Base64 and zero-width characters, to a coded text that the same route decodes from its last step back. Each step says what its way back loses; copy the route as a key sheet or a link.',
  regexlab:
    'Interactive regex tester and builder. Live match highlighting, capture groups, replace mode, and common pattern library.',
  pattern:
    'Word finder and pattern matcher. Search by wildcard patterns, digit codes, or regex against a full dictionary.',
  anagram:
    'Anagram solver and checker — find the words in a set of letters, one or several, and check a reading against an anagram letter by letter as 17th-century prints counted them: the priority anagrams of Galileo, Kepler, Huygens, Hooke and Newton.',
  diff: 'Text diff and comparison tool. Side-by-side, inline, and unified views with edit distance and similarity percentage.',
  hash: 'Hash generator — MD5, SHA-1, SHA-256, SHA-384, SHA-512. Generate cryptographic hash digests for any text.',
  fibonacci:
    'Fibonacci sequence generator and prime number checker. Factor numbers and explore mathematical sequences.',
  nexus:
    'Gematria Nexus — a name, word, number or birth date through every gematria cipher, the core numbers, Greer’s tarot birth and year cards, the Western and Chinese signs, the Moon and the day’s planet, and where they converge. Compare two entries side by side.',
  numerochart:
    'Numerology chart from a full birth name and birth date: Life Path, Expression, Soul Urge, Personality, pinnacles, challenges, cycles and letters, read in the words of L. Dow Balliett (1904) and Clifford Cheasley (1916), with where each position was first printed.',
  gematria:
    'Gematria calculator with Hebrew, English and custom ciphers — Agrippa’s Latin numbers (1533) with his arithmancy by nine and twelve; Hebrew fourteen ways, among them the kinds of Cordovero’s Pardes Rimonim — inclusive, squared and filled (milui) — and the four fillings of the Name; the words of Strong’s Hebrew dictionary and the verses of the Tanakh with the same value.',
  numerology:
    'Root Numbers — reduce any number, word or date to its root or master number (11, 22, 33), and a date to its life path.',
  dateresonance:
    'Date Resonance — symbolic profile for any date: numerology, moon phase, zodiac sign, planetary ruler, decanate, retrograde status, eclipses, and six calendar systems.',
  calendar:
    'Calendar converter for 16 calendars in both directions — Gregorian, Julian, Roman (Kalends, Nones and Ides, ab urbe condita), Hebrew, Islamic (Umm al-Qura and tabular), Persian, Coptic, Ethiopian, Indian National, Chinese, Korean, Japanese era, Buddhist, Minguo and Maya — and eight Masonic eras, with the yearly ritual cycles; Easter by the computus — golden number, epact, Sunday letter and paschal full moon, Gregorian and Julian, as Clavius, the Act of 1750 and the Prayer Book of 1662 give them — and the Hebrew year’s molads, Rosh Hashanah’s postponements and the tekufot of Samuel and Rav Adda after Maimonides.',
  duration:
    'Time tools: date duration in years, months, weeks, days and hours, with the month-end rule; a clock time’s mirror and opposite (11:10 → 12:50 and 5:40); and a time zone converter — a time in one city or IANA zone shown in others, with UTC offsets, daylight saving and the next change of each zone’s clocks.',
  timestamps:
    'Timestamp converter, both ways — Unix seconds and milliseconds, GPS week and seconds with leap seconds, Windows FILETIME, Chrome/WebKit, .NET ticks, Excel serial, NTP, Apple, XRP Ledger, MJD and Julian Date — exact to 100 nanoseconds.',
  tarot:
    'Tarot — draw Waite’s Celtic Cross or one card from the 78 cards of the first edition (1909), read in A. E. Waite’s own words (1911), or run the Golden Dawn’s Opening of the Key from Book T (1912): five operations, every card counted, paired and read in Book T’s words.',
  iching:
    'I Ching — cast a hexagram by three coins or the yarrow stalks and read it by Zhu Xi’s rules; all 64 hexagrams in Chinese with James Legge’s translation of 1882 — judgment, Great Image and lines — each with its nuclear hexagram after Wu Cheng and its place in Fu Xi’s order, read as binary by Leibniz, and the trigrams of the Shuo Kua.',
  geomancy:
    'Geomancy — cast or set four Mothers and read the shield chart of the sixteen figures in the words of Christopher Cattan (1591) and Agrippa’s Of Geomancy (1655), with each figure’s planet, sign and element as Agrippa and Cattan give them.',
  kabbalah:
    'Tree of Life — the ten sefirot and 22 paths of the Kabbalah, interactive, with divine names, archangels, Hebrew letters, tarot trumps and Golden Dawn attributions.',
  atlas:
    'Correspondence Atlas — the sefirot, planets, signs, elements and Hebrew letters with the 36 decans, the tarot, the 72 angels, the lunar mansions, the fixed stars and the geomantic figures: every link with its tradition and source — the Golden Dawn (777, Book T), Ptolemy and Lilly, Agrippa, al-Bīrūnī, the geomancers.',
  symbolatlas:
    'Symbology — structured explorer of alchemical, Masonic, Rosicrucian, Hermetic, Golden Dawn, Egyptian, and esoteric Christian symbols with correspondences.',
  symbolicscripts:
    'Symbolic Scripts — write text in Elder Futhark runes, Ogham and Enochian, and in magical alphabets traced from their sources: Agrippa’s Theban, Celestial, Malachim and Passing the River (1533), Trithemius’s Theban (1518), Ambrogio’s alphabets of Raphael, Solomon and Apollonius (1539), Hildegard of Bingen’s unknown letters and 82 of Ibn Waḥshiyya’s alphabets as Hammer printed them (1806).',
  zodiac:
    'Zodiac sign calculator from the Sun’s actual position — the exact moment it changes sign on a cusp day, element, modality, rulers, exaltation, detriment and fall, decan (face) and term — and the Chinese sign from the lunar new year.',
  astronomy:
    'Ephemeris — daily positions of the Sun, Moon and planets, sign ingresses, stations and retrograde periods, new and full moons, void-of-course Moon, eclipses and aspects, from VSOP87 and checked against JPL Horizons.',
  hellenistic:
    'Hellenistic astrology — essential dignities and twelfth-parts, sect, the hermetic lots and al-Bīrūnī’s ninety-seven, annual profections, zodiacal releasing from Spirit or Fortune after Vettius Valens, the Persian firdaria, and the hyleg by Ptolemy’s rules with Lilly’s alcochodon, al-Bīrūnī’s kadkhudā and the almuten.',
  planethours:
    'Planetary hours calculator: the unequal Chaldean hours of day and night for any date and place, with the day ruler, each hour’s name and angel from the Heptameron (1655) and the day’s angels, king of the air and conjuration.',
  acrostics:
    'Acrostic search — words spelled down the first or last letters of lines in Virgil, Aratus, Shakespeare’s Sonnets and First Folio, the Hebrew Bible or your own text, each count beside the number chance gives, every word of a text’s own vocabulary, and the Hebrew alphabetic acrostics.',
  gauquelin:
    'Gauquelin sectors — the 36 sectors of the Moon’s and each planet’s diurnal circle at a birth; the Mars effect recounted from the open birth data of Gauquelin’s 2,088 champions and the Comité Para, U.S., CFEPP and Ertel tests, and the Moon at the births of Gauquelin’s and Müller’s writers, beside shuffled controls and the published figures.',
  electional:
    'Electional search — find the times in a date range when the Moon is in chosen signs, not void of course, waxing or waning, chosen planets are direct, and the planetary day and hour fit, at any place.',
  astrocartography:
    'Astrocartography — where on the Earth each planet was rising, setting, culminating or below the earth at a birth: Jim Lewis’s lines by right ascension and declination on a world map, their crossings and their distance from any place; and Ptolemy’s 72 nations of the twelve signs (Tetrabiblos II.3, Ashmand 1822), 70 of them placed on the map.',
  lotbooks:
    'Lot books run as printed — the Middle English Chance of the Dice, Cattan’s Wheel of Pythagoras (1591), the Dodechedron of Fortune (1613), the Book of Fortune after Lorenzo Spirito (1698) and the Book of Fate (1822): dice, strokes or a name led through each book’s own tables to its answer, every step with its page.',
  natal:
    'Natal chart wheel — the Sun, Moon and planets from Mercury to Pluto, Ascendant and Midheaven, Whole Sign or Placidus houses for any date, time and place, the native described in William Lilly’s words (1647) with Ptolemy’s classes of the signs, and the temperament — sanguine, choleric, melancholic or phlegmatic — tallied by Lilly’s rule.',
  sacredgeometry:
    "Sacred geometry explorer — Flower of Life, Metatron's Cube, Vesica Piscis, Golden Spiral, Sri Yantra, and Platonic Solids with mathematical properties.",
  magicsquare:
    "Agrippa's seven planetary magic squares (kameas), Saturn to the Moon, as printed in De occulta philosophia II.22 — line sums, totals and every rotation and reflection.",
  sigil:
    'Sigil generator — create chaos magic sigils from intention statements. Remove vowels and duplicates, then render as connected glyph.',
  passwordgenerator:
    'Password generator and strength checker — random passwords, passphrases and pronounceable strings from crypto.getRandomValues with their entropy, and an analyzer for entropy, crack time, keyboard walks, dates, leet speak and common passwords. Nothing leaves the device.',
  rsa: 'RSA step by step — key generation, encryption and decryption — and attacks on weak keys: factoring, Wiener, shared primes, Håstad, common modulus.',
  emeraldtablet:
    'The Emerald Tablet of Hermes (Tabula Smaragdina) in ten versions side by side, sentence by sentence: the Arabic of the Book of the Secret of Creation and the Beirut manuscript with Ruska’s German (1926), Jābir’s Arabic, Hugo of Santalla’s Latin, the Secretum secretorum, the vulgate as first printed in 1541, the English of 1597, Kriegsmann’s German, and Isaac Newton’s Latin and English — with Ruska’s notes on where they part.',
  alchemicalsigns:
    'Alchemical and apothecaries’ signs as the Medicinisch-Chymisch- und Alchemistisches Oraculum (Ulm, 1755) tables them: every sign traced from the print beside its Latin and German, looked up by a word in Latin, German or English, read page by page as printed, and a recipe written in signs.',
  monas:
    'John Dee’s Monas hieroglyphica (Antwerp, 1564): the monad drawn step by step as his theorems build it — point, Sun, Moon, cross of the elements, horns of Aries — then Theorem XXIII’s construction, every part measured from one length to a whole of nine, and the ornament he allows; each theorem quoted whole in the Latin of the print.',
  atalanta:
    'Michael Maier’s Atalanta fugiens (Oppenheim, 1618): the fifty engraved emblems with their mottoes and epigrams in the Latin and German of the print and the English of Mellon MS 48, and each fugue for three voices transcribed from the print, drawn on staves, played voice by voice and tried against Maier’s rule for his canons, his misprints mended by his own errata.',
  chymicalwedding:
    'The name riddle of the Chymische Hochzeit Christiani Rosencreutz (Strasbourg, 1616), the virgin’s eight letters that make fifty-five, quoted from the German of 1616 and Foxcroft’s English of 1690 and solved by trying every numbering in three alphabets; whether ALCHIMIA, the usual answer, meets her conditions.',
  diffiehellman:
    'Diffie-Hellman key exchange visualizer. Step-by-step animated demonstration of how two parties agree on a shared secret over a public channel.',
  shamir:
    'Shamir’s secret sharing (1979) as SLIP-0039 standardises it for Trezor’s wallets: split a secret or a message into shares of words, any k of n rebuilding it and fewer telling nothing, in groups and with a passphrase; combine shares with every step shown — the checksum, Lagrange interpolation in GF(256), the digest, the Feistel decryption. Checked against the standard’s 45 test vectors. Shamir’s own construction modulo a prime worked on a number, and Liu’s 462 locks.',
  timelock:
    'Time-lock puzzles after Rivest, Shamir and Wagner (1996): lock a message for ten seconds to an hour behind t squarings in a row modulo a 2,048-bit number, which no number of computers can do faster, and open it by doing them, checked as they go by Shamir’s trick. LCS35, Rivest’s 35-year puzzle of 1999 solved in 2019, opened at once by its maker’s shortcut from the seed its message names.',
  quantum:
    'A quantum computer simulated in the browser, up to 20 qubits: Shor’s algorithm factoring a number step by step — the register in superposition, the work register measured, the quantum Fourier transform piling the probability on multiples of 2^t / r, the period read by continued fractions, the factors from gcd — and Grover’s search finding a marked item in about (π/4)√N steps, its amplitude growing as sin((2k + 1)θ). Why RSA falls and AES-256 keeps 128 bits.',
  qkd: 'BB84, the quantum key distribution of Bennett and Brassard (1984), simulated: photons polarised at random, measured at random, the public discussion of bases, a third of the bits compared to catch an eavesdropper by the errors she leaves (a quarter where she measures), and the key used as a one-time pad for a message. Their own 1984 example replayed from the page.',
  mlkem:
    'ML-KEM (FIPS 203), the post-quantum key encapsulation NIST standardised in 2024 from CRYSTALS-Kyber: generate a key pair, encrypt a message to an encapsulation key with ML-KEM and AES-256-GCM, open it with the decapsulation key, and see the 256 numbers decryption rounds to bits. Checked against NIST’s ACVP vectors; runs on your device.',
  aes: 'AES encryption and decryption in the browser: AES-GCM, AES-CBC and AES-CTR with PBKDF2 passphrase keys or raw 128/192/256-bit keys, plus HMAC-SHA-256/384/512 with verification. Runs locally on the Web Crypto API; keys never leave your device.',
  transits:
    'Transits and synastry — compare a natal chart with the planets at any moment, or two natal charts with each other: bi-wheel, aspects with orbs and applying or separating phase, and Whole Sign house overlays.',
  mansions:
    'The 28 mansions of the Moon — where the Moon stands now and when it enters each mansion, with Agrippa’s names, virtues and images (De occulta philosophia II.33 and II.46), the Arabic manāzil with their stars after al-Bīrūnī, and the Picatrix’s names.',
  vedic:
    'Vedic astrology (Jyotiṣa) on the Lahiri ayanāṃśa as the Indian Astronomical Ephemeris defines it: rāśi and navāṃśa charts in South or North Indian style, nakṣatras and pādas, Vimśottarī daśās with antardaśās, the day’s Pañcāṅga with ending times, and its breath and the five tattvas by the Science of Breath (Rama Prasad, 1894).',
  bazi:
    'Four Pillars (bazi) calculator: the year, month, day and hour of a birth as heavenly stems and earthly branches, counted from the solar terms (立春, the 節) on an exact Sun model, in local solar time; the Ten Gods, hidden stems, nayin and the luck pillars (大運), with the rules in the words of 三命通會 and the Yuanhai Ziping of 1600.',
  harmony:
    'Kepler’s harmony of the spheres (Harmonices mundi, 1619, Book V): each planet’s daily motion seen from the Sun at aphelion and perihelion recomputed from the ephemeris beside his printed table, the ratio as the interval he names — Saturn a major third, Mars a fifth, the Earth a semitone, mi fa mi — the harmonies between neighbours, his seven melodies as the woodcut prints them, drawn and played, and the planets’ pitches for any day.',
  fixedstars:
    'Fixed stars for astrology — 76 stars and clusters placed in the tropical zodiac for any date, the stars on a natal chart’s planets and angles and the parans of its day, Ptolemy’s planetary natures (Tetrabiblos I.9), Agrippa’s fifteen Behenian stars with their stones and plants, and the heliacal risings and settings of the stars and planets for any place and year, from Hesiod’s Athens to today.',
  horary:
    'Horary astrology in William Lilly’s manner (Christian Astrology, 1647): the question’s chart drawn square in Regiomontanus houses, every planet scored by his table of fortitudes and debilities, the significators’ aspects, reception, translation and collection of light, the Moon void of course.',
  progressions:
    'Progressions — secondary progressions (a day for a year) with progressed angles, lunation phase and the ingresses ahead; solar arc directions and when they perfect; Placidus’s primary directions by semi-arcs with his own measure of time; solar and lunar returns to the second, cast for any place.',
  nullcipher:
    'Null cipher decoder: extract a message hidden in cover text by letter position in each word, line or sentence, every n-th letter or word, letters after punctuation, or capitals. A scan ranks common constructions by English letter statistics.',
  wordsplit:
    'Word segmentation tool that restores the spaces in unspaced text such as decrypted ciphertext, using a frequency-ranked English word model, with close alternative readings and support for names you supply.',
  qrcode:
    'QR code generator and reader — a text made into a QR code by ISO/IEC 18004 with every choice shown (version, error correction L to H, the eight masks and their penalties, the Reed–Solomon blocks, each part of the symbol coloured), and a QR code read from an image, with the errors its correction repaired.',
  puzzlecodes:
    'Puzzle code translator — Braille (Unified English Braille), flag semaphore, International Code of Signals flags, the NATO spelling alphabet, tap code with sound, and phone keypad codes (T9, multi-tap), both ways where the code allows.',
  els:
    'ELS search — equidistant letter sequences (“Bible codes”) in the Torah, any book or the whole Tanakh, with the count chance gives from the letters’ frequencies and in shuffled letters, and the table around each sequence.',
  gates:
    'The 231 gates of Sefer Yetzirah — the 22 Hebrew letters on a wheel, every letter joined with every other: each letter’s 21 gates, a word traced round the wheel through its pairs, the orders its letters build, and the three mothers, seven doubles and twelve simples.',
  shemhamephorash:
    'The 72 Names of God (Shem ha-Mephorash) drawn from Exodus 14:19–21 in the Leningrad Codex — three verses of 72 letters read forward, backward and forward — with each name’s letters shown in the verses.',
  steganographia:
    'Trithemius’s Steganographia (1606) decoded: the spirit conjurations of Books I and II read by the rules of its Clavis, and the number tables of Book III, solved by Ernst and Reeds, decrypted number by number from the first edition, with a message hidden in a spirit’s numbers.',
  musical:
    'Musical cryptograms: text written as notes and played — German note names (BACH, ABEGG, Carnaval’s Sphinxes), Josquin’s soggetto cavato, Kircher’s and Schott’s musical alphabets, Michael Haydn’s secret notation and the French method of 1910 — each with its period examples as printed.',
  soyga:
    'Book of Soyga (Aldaraia sive Soyga vocor): its 36 tables of 36 × 36 letters made by the rule Jim Reeds found — the code word down the left column, each letter the one above moved on by the step of the one to its left — with the maker’s slips, every letter’s working, and a table from any name.',
  llull:
    'Ramon Llull’s Ars brevis (1308): the alphabet of nine letters, the four figures — principles, relations in three triangles, the 36 chambers and their evacuation, the turning circles — and the Table of 84 columns of 20 chambers, made by rule and checked against the prints of 1517 and 1617.',
  enochian:
    'John Dee and Edward Kelley’s Great Table (Cracow 1584) letter by letter from A True & Faithful Relation (1659): the four tables, every name of God and angel Dee drew from them by Ave’s rules, the Black Cross, the table reformed in 1587 and Dee’s square-number cipher.',
  temurah:
    'Temurah and Notarikon — the Kabbalistic letter-exchange alphabets Atbash, Albam, Atbah and Avgad with gematria values, the nine chambers of Aiq Bekar, and notarikon: a phrase’s initials and the runs of words in the Tanakh whose initials spell a word, against chance.',
  isopsephy:
    'Isopsephy and abjad calculator — Greek letter values with Milesian numerals (stigma, koppa, sampi, keraia), the words of Strong’s Greek dictionary and the New Testament verses with the same value, and Arabic abjad in Mashriqi and Maghribi order.',
  kameasigil:
    "Kamea sigils — trace a Hebrew name or a list of numbers across Agrippa's seven planetary magic squares, Saturn to Moon. Aiq Bekar reduction, numerals or Hebrew notes, SVG export.",
};

// ─── Smart "Open In" relevance map ──────────────────────────────────────────
// Per-tool: suggested (2–3 best next steps) + related (pipeline neighbors).
// Tools not listed fall back to group listing in the "More tools" section.

export const TOOL_RELEVANCE = {
  // Cryptanalysis & text
  layers: { suggested: ['cipherid', 'chain'], related: ['converter', 'fileforensics', 'xor'] },
  fileforensics: { suggested: ['lsb', 'layers'], related: ['hash', 'zerowidth', 'converter'] },
  audiostego: { suggested: ['fileforensics', 'layers'], related: ['lsb', 'morse'] },
  trackingdots: { suggested: ['lsb', 'fileforensics'], related: ['qrcode', 'audiostego'] },
  visualcrypto: { suggested: ['shamir', 'lsb'], related: ['xor', 'trackingdots', 'zerowidth', 'dnastego'] },
  dnastego: { suggested: ['visualcrypto', 'lsb'], related: ['zerowidth', 'nullcipher', 'substitution', 'timelock'] },
  cipherid: {
    suggested: ['cryptanalysis', 'substitution'],
    related: ['caesar', 'vigenere', 'nullcipher', 'wordsplit', 'chain', 'diff'],
  },
  cryptanalysis: {
    suggested: ['substitution', 'vigenere', 'caesar'],
    related: ['cipherid', 'diff', 'pattern', 'chain', 'regexlab'],
  },
  unicity: { suggested: ['substitution', 'cryptanalysis'], related: ['randomness', 'vigenere', 'playfair', 'enigma'] },
  randomness: { suggested: ['fileforensics', 'cryptanalysis'], related: ['unicity', 'passwordgenerator', 'hash', 'xor', 'solitaire'] },
  lindiff: { suggested: ['aes', 'unicity'], related: ['randomness', 'aca', 'cryptanalysis'] },
  nomenclator: { suggested: ['wordsplit', 'archive'], related: ['substitution', 'bookcipher', 'cryptanalysis'] },
  cyclometer: { suggested: ['enigma', 'banburismus'], related: ['archive', 'cryptanalysis'] },
  banburismus: { suggested: ['enigma', 'cyclometer'], related: ['archive', 'cryptanalysis'] },
  archive: {
    suggested: ['cryptanalysis', 'cipherid'],
    related: ['substitution', 'vigenere', 'playfair', 'enigma', 'bookcipher', 'gridciphers'],
  },
  pattern: { suggested: ['substitution', 'anagram'], related: ['cryptanalysis', 'cipherid', 'diff', 'regexlab'] },
  anagram: { suggested: ['pattern', 'wordsplit'], related: ['diff', 'cryptanalysis'] },
  diff: { suggested: ['cryptanalysis', 'cipherid'], related: ['chain'] },
  chain: { suggested: ['layers', 'cipherid'], related: ['aca', 'enigma', 'converter', 'zerowidth', 'diff'] },
  regexlab: { suggested: ['pattern', 'cipherid'], related: ['cryptanalysis', 'diff', 'chain'] },
  // Ciphers
  caesar: {
    suggested: ['cipherid', 'wordsplit'],
    related: ['cryptanalysis', 'diff', 'substitution', 'vigenere', 'chain'],
  },
  vigenere: {
    suggested: ['wordsplit', 'cryptanalysis'],
    related: ['cipherid', 'diff', 'substitution', 'caesar', 'chain'],
  },
  substitution: {
    suggested: ['cryptanalysis', 'pattern'],
    related: ['wordsplit', 'cipherid', 'diff', 'caesar', 'vigenere'],
  },
  playfair: { suggested: ['cipherid', 'cryptanalysis'], related: ['diff', 'substitution', 'chain'] },
  enigma: { suggested: ['cipherid', 'cryptanalysis'], related: ['diff', 'chain'] },
  hagelin: { suggested: ['enigma', 'cylinder'], related: ['lorenz', 'archive'] },
  lorenz: { suggested: ['enigma', 'xor'], related: ['hagelin', 'archive'] },
  purple: { suggested: ['enigma', 'sigaba'], related: ['archive', 'cryptanalysis'] },
  sigaba: { suggested: ['enigma', 'hagelin'], related: ['lorenz', 'archive'] },
  typex: { suggested: ['enigma', 'sigaba'], related: ['purple', 'archive'] },
  chaocipher: { suggested: ['archive', 'cryptanalysis'], related: ['enigma', 'cylinder'] },
  cylinder: { suggested: ['hagelin', 'chaocipher'], related: ['cryptanalysis', 'archive'] },
  vic: { suggested: ['gridciphers', 'transposition'], related: ['archive', 'cryptanalysis'] },
  solitaire: { suggested: ['vigenere', 'vic'], related: ['cryptanalysis', 'xor'] },
  transposition: {
    suggested: ['cipherid', 'wordsplit'],
    related: ['cryptanalysis', 'diff', 'chain', 'gridciphers'],
  },
  visualciphers: { suggested: ['cipherid', 'symbolicscripts'], related: ['puzzlecodes', 'archive', 'substitution', 'chain'] },
  bookcipher: { suggested: ['cipherid', 'diff'], related: ['substitution', 'chain'] },
  gridciphers: { suggested: ['cipherid', 'cryptanalysis'], related: ['playfair', 'transposition', 'diff', 'chain'] },
  aca: { suggested: ['cipherid', 'cryptanalysis'], related: ['vigenere', 'transposition', 'playfair', 'gridciphers', 'substitution'] },
  // Encodings & steganography
  morse: { suggested: ['puzzlecodes'], related: ['chain', 'diff', 'ascii'] },
  converter: { suggested: ['cipherid', 'chain', 'ascii'], related: ['diff'] },
  roman: { suggested: ['converter'], related: ['numerology'] },
  timestamps: { suggested: ['converter', 'calendar'], related: ['duration', 'cipherid', 'hash'] },
  ascii: { suggested: ['converter', 'chain'], related: ['diff', 'hash'] },
  zerowidth: { suggested: ['layers', 'ascii'], related: ['lsb', 'audiostego', 'nullcipher', 'diff', 'cipherid'] },
  lsb: { suggested: ['zerowidth', 'hash'], related: ['diff', 'cipherid', 'visualcrypto'] },
  // Modern crypto
  xor: { suggested: ['cipherid', 'cryptanalysis'], related: ['qkd', 'converter', 'diff', 'chain'] },
  aes: { suggested: ['hash', 'converter'], related: ['mlkem', 'xor', 'rsa', 'diffiehellman', 'passwordgenerator', 'shamir'] },
  hash: { suggested: ['passwordgenerator', 'aes'], related: ['rsa', 'diffiehellman', 'converter'] },
  passwordgenerator: { suggested: ['hash', 'aes'], related: ['diffiehellman', 'rsa', 'shamir'] },
  rsa: { suggested: ['diffiehellman', 'hash'], related: ['mlkem', 'aes', 'converter'] },
  diffiehellman: { suggested: ['rsa', 'mlkem'], related: ['aes', 'hash', 'passwordgenerator', 'converter'] },
  mlkem: { suggested: ['aes', 'qkd'], related: ['rsa', 'diffiehellman', 'hash', 'xor'] },
  quantum: { suggested: ['rsa', 'mlkem'], related: ['aes', 'qkd', 'diffiehellman'] },
  shamir: { suggested: ['aes', 'passwordgenerator'], related: ['visualcrypto', 'mlkem', 'qkd', 'hash', 'xor'] },
  timelock: { suggested: ['rsa', 'aes'], related: ['shamir', 'quantum', 'diffiehellman', 'dnastego'] },
  qkd: { suggested: ['xor', 'mlkem'], related: ['diffiehellman', 'aes'] },
  // Gematria & numerology, Kabbalah
  fibonacci: { suggested: ['numerology', 'nexus'], related: ['sacredgeometry', 'dateresonance'] },
  numerology: { suggested: ['nexus', 'numerochart'], related: ['gematria', 'dateresonance'] },
  gematria: {
    suggested: ['nexus'],
    related: ['isopsephy', 'temurah', 'numerology', 'numerochart', 'dateresonance', 'kabbalah'],
  },
  nexus: { suggested: ['numerochart', 'gematria'], related: ['gematria', 'numerology', 'dateresonance'] },
  numerochart: { suggested: ['nexus'], related: ['gematria', 'numerology', 'dateresonance'] },
  magicsquare: { suggested: ['kameasigil', 'planethours'], related: ['numerology', 'atlas'] },
  // Divination
  tarot: { suggested: ['nexus', 'kabbalah'], related: ['iching', 'geomancy', 'numerology', 'zodiac'] },
  iching: { suggested: ['geomancy', 'tarot'], related: ['atlas', 'numerology'] },
  geomancy: { suggested: ['atlas', 'iching'], related: ['tarot', 'zodiac', 'planethours', 'astronomy'] },
  // Kabbalah, symbols & scripts
  kabbalah: { suggested: ['atlas', 'tarot', 'gematria'], related: ['temurah', 'numerology', 'zodiac', 'planethours'] },
  atlas: {
    suggested: ['kabbalah', 'tarot', 'zodiac'],
    related: ['fixedstars', 'mansions', 'geomancy', 'shemhamephorash', 'planethours', 'magicsquare', 'gematria', 'nexus'],
  },
  symbolatlas: {
    suggested: ['atlas', 'sacredgeometry'],
    related: ['kabbalah', 'tarot', 'zodiac', 'gematria', 'planethours'],
  },
  symbolicscripts: { suggested: ['visualciphers', 'sigil'], related: ['gematria', 'atlas', 'kabbalah'] },
  sigil: { suggested: ['gematria', 'numerology'], related: ['kameasigil', 'visualciphers', 'symbolicscripts'] },
  kameasigil: { suggested: ['gematria', 'magicsquare'], related: ['sigil', 'planethours', 'atlas', 'kabbalah'] },
  qrcode: { suggested: ['layers', 'puzzlecodes'], related: ['fileforensics', 'converter', 'morse'] },
  puzzlecodes: {
    suggested: ['cipherid', 'morse'],
    related: ['visualciphers', 'chain', 'caesar', 'anagram'],
  },
  temurah: { suggested: ['gematria', 'kabbalah'], related: ['caesar', 'kameasigil', 'symbolicscripts', 'atlas'] },
  els: { suggested: ['gematria', 'temurah'], related: ['kabbalah', 'cryptanalysis', 'pattern'] },
  gates: { suggested: ['temurah', 'kabbalah'], related: ['gematria', 'els', 'anagram'] },
  shemhamephorash: { suggested: ['gematria', 'gates'], related: ['kabbalah', 'atlas', 'els', 'kameasigil'] },
  enochian: { suggested: ['symbolicscripts', 'magicsquare'], related: ['kabbalah', 'gridciphers', 'archive'] },
  steganographia: { suggested: ['zerowidth', 'nullcipher'], related: ['archive', 'planethours'] },
  musical: { suggested: ['steganographia', 'morse'], related: ['audiostego', 'puzzlecodes'] },
  soyga: { suggested: ['enochian', 'magicsquare'], related: ['gates', 'symbolicscripts'] },
  llull: { suggested: ['gates', 'atlas'], related: ['anagram', 'symbolicscripts'] },
  isopsephy: { suggested: ['gematria', 'nexus'], related: ['numerology', 'roman', 'symbolicscripts', 'numerochart'] },
  sacredgeometry: {
    suggested: ['fibonacci', 'atlas'],
    related: ['kabbalah', 'magicsquare', 'numerology', 'zodiac', 'symbolatlas'],
  },
  // Astrology & calendars
  natal: { suggested: ['transits', 'hellenistic'], related: ['zodiac', 'planethours', 'astronomy', 'dateresonance'] },
  transits: { suggested: ['natal', 'progressions'], related: ['hellenistic', 'zodiac', 'astronomy', 'planethours', 'dateresonance'] },
  progressions: { suggested: ['transits', 'hellenistic'], related: ['natal', 'astronomy', 'zodiac'] },
  horary: { suggested: ['hellenistic', 'natal'], related: ['fixedstars', 'electional', 'planethours'] },
  harmony: { suggested: ['astronomy', 'musical'], related: ['atalanta', 'natal', 'atlas'] },
  fixedstars: { suggested: ['natal', 'astronomy'], related: ['atlas', 'symbolatlas', 'electional'] },
  vedic: { suggested: ['natal', 'planethours'], related: ['zodiac', 'astronomy', 'calendar', 'bazi'] },
  bazi: { suggested: ['calendar', 'vedic'], related: ['natal', 'zodiac', 'iching'] },
  mansions: { suggested: ['electional', 'planethours'], related: ['fixedstars', 'astronomy', 'vedic'] },
  zodiac: {
    suggested: ['natal', 'hellenistic'],
    related: ['dateresonance', 'astronomy', 'nexus', 'calendar'],
  },
  astronomy: { suggested: ['dateresonance', 'hellenistic'], related: ['planethours', 'zodiac', 'calendar', 'atlas'] },
  hellenistic: { suggested: ['astronomy', 'atlas'], related: ['zodiac', 'planethours', 'dateresonance', 'tarot'] },
  acrostics: { suggested: ['nullcipher', 'els'], related: ['archive', 'pattern', 'zerowidth'] },
  gauquelin: { suggested: ['natal', 'planethours'], related: ['els', 'astronomy', 'electional'] },
  planethours: {
    suggested: ['dateresonance', 'zodiac'],
    related: ['astronomy', 'hellenistic', 'calendar', 'magicsquare'],
  },
  astrocartography: { suggested: ['natal', 'fixedstars'], related: ['zodiac', 'hellenistic', 'progressions'] },
  lotbooks: { suggested: ['geomancy', 'tarot'], related: ['iching', 'numerology'] },
  electional: {
    suggested: ['astronomy', 'planethours'],
    related: ['natal', 'hellenistic', 'transits', 'calendar'],
  },
  // Alchemy & Hermetica
  atalanta: { suggested: ['emeraldtablet', 'musical'], related: ['alchemicalsigns', 'chymicalwedding', 'monas'] },
  chymicalwedding: { suggested: ['gematria', 'anagram'], related: ['visualciphers', 'symbolicscripts', 'numerology'] },
  emeraldtablet: { suggested: ['chymicalwedding', 'atlas'], related: ['symbolatlas', 'kabbalah'] },
  alchemicalsigns: { suggested: ['symbolatlas', 'atlas'], related: ['emeraldtablet', 'symbolicscripts', 'visualciphers'] },
  monas: { suggested: ['emeraldtablet', 'atlas'], related: ['sacredgeometry', 'symbolatlas', 'enochian'] },
  calendar: { suggested: ['dateresonance'], related: ['planethours', 'duration', 'timestamps'] },
  dateresonance: { suggested: ['nexus', 'calendar'], related: ['astronomy', 'planethours', 'numerology', 'zodiac'] },
  duration: { suggested: ['dateresonance', 'numerology'], related: ['calendar', 'timestamps'] },
};
